<template>
  <v-card flat class="board-pp" ref="fullscreenRoot">
    <!-- ===================== TOOLBAR ===================== -->
    <div class="toolbar">
      <!-- ===================== FILTROS (en la misma fila de la toolbar, label dentro del input) ===================== -->
      <div v-if="!esFullscreen" class="filtros">
        <VueCtkDateTimePicker v-model="filtros.inicio" :disabled="cargando" :max-date="filtros.fin || undefined"
          format="YYYY-MM-DD HH:mm" formatted="YYYY-MM-DD HH:mm" first-day-of-week="1" noHeader overlay
          :locale="currentLocale" :label="$t('Reports.ProductionPlan.filters.start')"
          :button-now-translation="$t('Reports.ProductionPlan.chart.now')" class="f-input" />
        <VueCtkDateTimePicker v-model="filtros.fin" :disabled="cargando" :min-date="filtros.inicio || undefined"
          format="YYYY-MM-DD HH:mm" formatted="YYYY-MM-DD HH:mm" first-day-of-week="1" noHeader overlay
          :locale="currentLocale" :label="$t('Reports.ProductionPlan.filters.end')"
          :button-now-translation="$t('Reports.ProductionPlan.chart.now')" class="f-input" />
        <v-autocomplete v-model="filtros.idsArea" :items="catAreas" multiple chips small-chips deletable-chips clearable
          dense outlined hide-details :disabled="cargando" :label="$t('Reports.ProductionPlan.filters.areas')"
          class="f-input f-input--areas" />
        <div class="f-input f-sel">
          <span class="f-sel-lbl">{{ $t('Reports.ProductionPlan.filters.view') }}:</span>
          <v-select v-model="vista" :items="vistaOpciones" item-text="text" item-value="value" dense outlined
            hide-details :disabled="cargando" class="f-sel-input" />
        </div>
        <v-autocomplete v-if="gamemode" v-model="filtros.idsInstancia" :items="catInstancias" multiple chips small-chips
          deletable-chips clearable dense outlined hide-details :disabled="cargando"
          :label="$t('Reports.ProductionPlan.filters.instances')" class="f-input f-input--areas" />
        <v-autocomplete v-if="gamemode" v-model="filtros.idsTrabajoOrden" :items="catOrdenes" multiple chips small-chips
          deletable-chips clearable dense outlined hide-details :disabled="cargando"
          :label="$t('Reports.ProductionPlan.filters.workOrders')" class="f-input f-input--areas" />
        <v-btn small color="primary" class="f-aplicar" :loading="cargando" @click="cargar">
          <v-icon left small>mdi-magnify</v-icon> {{ $t('Reports.ProductionPlan.filters.search') }}
        </v-btn>
      </div>

      <!-- Acciones a la derecha: timer de recarga, config (gamemode) y pantalla completa. -->
      <div class="tb-actions">
        <span v-if="!esFullscreen" class="auto-refresh" :class="{ 'is-loading': cargando }"
          :title="$t('Reports.ProductionPlan.toolbar.autoRefreshTitle')">
          <v-icon small :class="{ 'spin': cargando }">mdi-autorenew</v-icon>
          {{ restanteTexto }}
        </span>
        <v-btn v-if="gamemode" small outlined color="orange darken-2" class="pp-fs-btn" @click="abrirConfig"
          :title="$t('Reports.ProductionPlan.settings.button')">
          <v-icon left small>mdi-cog-outline</v-icon>
          {{ $t('Reports.ProductionPlan.settings.button') }}
        </v-btn>

        <v-btn small outlined color="primary" class="pp-fs-btn" @click="toggleFullscreen"
          :title="esFullscreen ? $t('Reports.ProductionPlan.toolbar.exitFullscreen') : $t('Reports.ProductionPlan.toolbar.fullscreen')">
          <v-icon left small>{{ esFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
          {{ esFullscreen ? $t('Reports.ProductionPlan.toolbar.exit') : $t('Reports.ProductionPlan.toolbar.fullscreen') }}
        </v-btn>
      </div>
    </div>

    <!-- ===================== TARJETAS RESUMEN: (Planta + Máquinas) | (Avance + Programadas), mismo renglón ===================== -->
    <div class="pp-toprow">
      <!-- Card 1: Planta + Máquinas activas -->
      <div class="pp-topcard pp-topcard--maq">
        <!-- Planta: total de máquinas activas de la planta + nombre de la instancia. -->
        <div class="pp-sec pp-sec--planta" @click="dialogActivas = true"
          :title="$t('Reports.ProductionPlan.activeDialog.title')">
          <div class="pp-sec-title">{{ $t('Reports.ProductionPlan.kpi.plant') }}</div>
          <v-progress-circular v-if="maquinasActivasCargando" indeterminate color="#2d84da" :size="20" :width="3"
            class="pp-sec-loader" />
          <template v-else>
            <div class="pp-ma-val">{{ maquinasActivas.activas }}/{{ maquinasActivas.total }}</div>
            <div class="pp-ma-area pp-ma-area--planta">{{ nombrePlanta }}</div>
          </template>
        </div>

        <!-- Máquinas activas: una columna por nave/área (activas/esperadas). -->
        <div class="pp-sec pp-sec--maq" @click="dialogActivas = true"
          :title="$t('Reports.ProductionPlan.activeDialog.title')">
          <div class="pp-sec-title pp-sec-title--center">{{ $t('Reports.ProductionPlan.kpi.activeMachines') }}</div>
          <v-progress-circular v-if="maquinasActivasCargando" indeterminate color="#2d84da" :size="20" :width="3"
            class="pp-sec-loader" />
          <div v-else class="pp-ma-cols">
            <div v-for="a in resumenPorArea" :key="a.id" class="pp-ma-col">
              <span class="pp-ma-val">{{ a.activas }}/{{ a.esperadas }}</span>
              <span class="pp-ma-area">{{ a.nombre }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Card 2: Avance de piezas + Programadas al momento -->
      <div class="pp-topcard pp-topcard--avance">
        <!-- Avance de piezas: piezas · barra · porcentaje en una línea. -->
        <div class="pp-sec pp-sec--avance">
          <div class="pp-sec-title pp-sec-title--center">{{ $t('Reports.ProductionPlan.summary.totalProgress') }}</div>
          <div class="pp-avance-row">
            <span class="rs-piezas">{{ fmtNum(avance.piezasOk) }} / {{ fmtNum(avance.piezasObjetivo) }}</span>
            <div class="rs-bar">
              <div class="rs-fill" :style="{ width: Math.min(100, porcentajeAvance) + '%', background: colorAvance }" />
            </div>
            <span class="rs-pct" :style="{ color: colorAvance }">{{ porcentajeAvance }}%</span>
          </div>
        </div>

        <!-- Programadas al momento: etiqueta normal, número en negritas. -->
        <div class="pp-sec pp-sec--prog">
          <div class="pp-sec-title pp-sec-title--normal">{{ $t('Reports.ProductionPlan.summary.scheduledSoFar') }}</div>
          <div class="rs-now-val">{{ fmtNum(avance.piezasOk) }} / {{ fmtNum(piezasProgramadasHastaAhora) }}</div>
        </div>
      </div>
    </div>

    <!-- ===================== LEYENDA (fija al hacer scroll) ===================== -->
    <div class="pp-sticky-top" ref="stickyTop">
      <div class="legend">
        <span class="lg"><i class="i-plan" /> {{ $t('Reports.ProductionPlan.legend.plan') }}</span>
        <span v-for="(b, i) in bandasLeyenda" :key="i" class="lg">
          <i class="sw" :style="{ background: b.color }" /> {{ b.etiqueta || rangoBandaLeyenda(i) }}
        </span>
        <span class="lg"><i class="i-finalizada" /> {{ $t('Reports.ProductionPlan.legend.finished') }}</span>
      </div>
    </div>

    <!-- ===================== GRÁFICA ===================== -->
    <div class="grafica-box">
      <!-- Barra de fechas: eje de tiempo fijo (sticky) y sincronizado con el gráfico principal. -->
      <div v-show="maquinasFiltradas.length > 0" ref="ejeFecha" class="pp-eje-fecha"
        :style="{ top: stickyTopH + 'px' }" />
      <div v-if="cargando && !tablero" class="cp-empty">{{ $t('Reports.ProductionPlan.empty.loadingBoard') }}</div>
      <div v-else-if="error" class="cp-empty">{{ $t('Reports.ProductionPlan.empty.boardError') }}</div>
      <div v-else-if="maquinasFiltradas.length === 0" class="cp-empty">{{ $t('Reports.ProductionPlan.empty.noMachines')
        }}</div>
      <div v-show="maquinasFiltradas.length > 0" ref="grafica" class="grafica-tablero"
        :style="{ height: altoGrafica + 'px' }" />
    </div>

    <!-- Dialog de detalle de fragmento -->
    <v-dialog v-model="dialogFragmento" max-width="560">
      <v-card class="frag-card">
        <v-card-title class="frag-title" :class="{ 'frag-title--fin': fragmento.finalizada }">
          <i v-if="fragmento.esReal" class="frag-dot" :class="{ 'frag-dot--fin': fragmento.finalizada }"
            :style="{ background: fragmento.colorHex }" />
          <span>{{ tipoLabel }}<template v-if="fragmento.area"> · {{ fragmento.area }}</template></span>
          <v-chip v-if="fragmento.esReal && fragmento.enCurso" color="primary" x-small class="ml-2">
            {{ $t('Reports.ProductionPlan.detail.inProgress') }}
          </v-chip>
          <v-spacer />
          <span v-if="fragmento.esReal" class="frag-banda" :style="{ color: fragmento.colorHex }">
            {{ bandaLabel }}
          </span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-3">
          <!-- Identificación -->
          <div class="frag-sec">{{ $t('Reports.ProductionPlan.detail.identification') }}</div>
          <div class="frag-grid">
            <div class="frag-row"><span>{{ $t('Reports.ProductionPlan.detail.machine') }}</span><b>{{
                fragmento.maquina }}</b></div>
            <div class="frag-row"><span>{{ $t('Reports.ProductionPlan.detail.mold') }}</span><b>{{ fragmento.molde
                }}</b></div>
            <div class="frag-row frag-row--wide"><span>{{ $t('Reports.ProductionPlan.detail.orderAbbr') }}</span><b>{{ fragmento.clave
                }}</b></div>
          </div>

          <!-- Tiempos -->
          <div class="frag-sec">{{ $t('Reports.ProductionPlan.detail.times') }}</div>
          <div class="frag-grid">
            <div class="frag-row"><span>{{ $t('Reports.ProductionPlan.detail.start') }}</span><b>{{ fragmento.inicio
                }}</b></div>
            <div class="frag-row"><span>{{ $t('Reports.ProductionPlan.detail.end') }}{{ fragmento.enCurso ? `
                (${$t('Reports.ProductionPlan.detail.projected')})` : '' }}</span><b>{{ fragmento.fin
                }}</b>
            </div>
            <!-- Duración: real = tiempo real; plan al 100% = tiempo programado base. -->
            <div v-if="fragmento.esReal || fragmento.holguraSeg <= 0" class="frag-row">
              <span>{{ fragmento.esReal ? $t('Reports.ProductionPlan.detail.realDuration') :
                $t('Reports.ProductionPlan.detail.scheduledDuration') }}</span><b>{{ fragmento.duracion }}</b>
            </div>
            <div v-if="fragmento.esReal && fragmento.duracionTeorica" class="frag-row">
              <span>{{ $t('Reports.ProductionPlan.detail.theoreticalDuration') }}</span><b>{{ fragmento.duracionTeorica
                }}</b>
            </div>
            <!-- Plan ajustado: 100% = duración base; N% = duración total según la eficiencia. -->
            <div v-if="!fragmento.esReal && fragmento.holguraSeg > 0" class="frag-row">
              <span>{{ $t('Reports.ProductionPlan.detail.efficiency') }} 100%</span><b>{{ fragmento.duracionBase }}</b>
            </div>
            <div v-if="!fragmento.esReal && fragmento.holguraSeg > 0" class="frag-row">
              <span>{{ $t('Reports.ProductionPlan.detail.efficiency') }} {{ eficienciaPct }}%</span><b>{{ fragmento.duracion }}</b>
            </div>
            <div class="frag-row"><span>{{ $t('Reports.ProductionPlan.detail.scheduledTc') }}</span><b>{{ fragmento.tc ?
              fragmento.tc + ' seg' : '—' }}</b>
            </div>
          </div>

          <!-- Producción -->
          <div class="frag-sec">{{ $t('Reports.ProductionPlan.detail.production') }}</div>
          <div class="frag-grid">
            <div class="frag-row"><span>{{ $t('Reports.ProductionPlan.detail.target') }}</span><b>{{
              fmtNum(fragmento.objetivo) }} {{ $t('Reports.ProductionPlan.summary.pieces') }}</b></div>
            <template v-if="fragmento.esReal">
              <div v-if="fragmento.programadasAhora != null" class="frag-row">
                <span>{{ $t('Reports.ProductionPlan.detail.scheduledSoFar') }}</span>
                <b>{{ fmtNum(fragmento.programadasAhora) }} {{ $t('Reports.ProductionPlan.summary.pieces') }}</b>
              </div>
              <div class="frag-row"><span>{{ $t('Reports.ProductionPlan.detail.producedOk') }}</span><b>{{
                fmtNum(fragmento.ok) }} {{ $t('Reports.ProductionPlan.summary.pieces') }}</b></div>
              <div class="frag-row"><span>{{ $t('Reports.ProductionPlan.detail.notOk') }}</span><b>{{
                fmtNum(fragmento.noOk) }} {{ $t('Reports.ProductionPlan.summary.pieces') }}</b></div>
              <div class="frag-row"><span>{{ $t('Reports.ProductionPlan.detail.missing') }}</span><b>{{
                fmtNum(fragmento.faltantes) }} {{ $t('Reports.ProductionPlan.summary.pieces') }}</b></div>
              <div class="frag-row">
                <span>{{ $t('Reports.ProductionPlan.detail.compliance') }}</span>
                <b :style="{ color: fragmento.colorHex }">{{ fragmento.cumplimiento }}%</b>
              </div>
              <div class="frag-row"><span>{{ $t('Reports.ProductionPlan.detail.timeGap') }}</span><b>{{ desfaseTiempo
                  }}</b></div>
              <div class="frag-row"><span>{{ $t('Reports.ProductionPlan.detail.percentGap') }}</span><b>{{
                  desfasePctTexto }}</b></div>
              <div class="frag-row frag-row--wide">
                <span>{{ $t('Reports.ProductionPlan.detail.range') }}</span>
                <b class="frag-rango" :style="{ color: fragmento.colorHex }">
                  <i class="frag-dot" :class="{ 'frag-dot--fin': fragmento.finalizada }"
                    :style="{ background: fragmento.colorHex, marginRight: '6px' }" />
                  {{ bandaLabel }}
                </b>
              </div>
            </template>
          </div>

          <!-- Barra de cumplimiento -->
          <div v-if="fragmento.esReal" class="frag-progress">
            <div class="frag-progress-bar" :class="{ 'frag-progress-bar--fin': fragmento.finalizada }">
              <div class="frag-progress-fill"
                :style="{ width: fragmento.cumplimientoBar + '%', background: fragmento.colorHex }" />
            </div>
            <span class="frag-progress-txt">{{ fmtNum(fragmento.ok) }} / {{ fmtNum(fragmento.objetivo) }}</span>
          </div>

          <!-- Detalle por operación (cada operación = una cavidad; corren en paralelo) -->
          <template v-if="fragmento.operaciones && fragmento.operaciones.length">
            <div class="frag-sec">{{ $t('Reports.ProductionPlan.detail.operations') }} · {{ fragmento.operaciones.length
              }} {{ $t('Reports.ProductionPlan.detail.cavities') }}</div>
            <table class="frag-ops">
              <thead>
                <tr>
                  <th>{{ $t('Reports.ProductionPlan.detail.articleCode') }}</th>
                  <th class="num">TC</th>
                  <th class="num">{{ $t('Reports.ProductionPlan.detail.scheduled') }}</th>
                  <template v-if="fragmento.esReal">
                    <th class="num">{{ $t('Reports.ProductionPlan.detail.done') }}</th>
                    <th class="num">OK</th>
                    <th class="num">{{ $t('Reports.ProductionPlan.detail.notOk') }}</th>
                  </template>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(op, i) in fragmento.operaciones" :key="op.idOperacion || i">
                  <td>{{ op.codigo || '—' }}</td>
                  <td class="num">{{ op.tc ? op.tc + ' s' : '—' }}</td>
                  <td class="num">{{ fmtNum(op.esperadas) }}</td>
                  <template v-if="fragmento.esReal">
                    <td class="num">{{ fmtNum(op.totales) }}</td>
                    <td class="num">{{ fmtNum(op.ok) }}</td>
                    <td class="num">{{ fmtNum(op.noOk) }}</td>
                  </template>
                </tr>
              </tbody>
            </table>
          </template>
        </v-card-text>
        <v-card-actions class="frag-actions">
          <v-btn v-if="fragmento.noIniciada" text color="error" @click="abrirCambioInstancia">
            <v-icon left small>mdi-swap-horizontal</v-icon>{{ $t('Reports.ProductionPlan.detail.changeInstance') }}
          </v-btn>
          <v-btn v-if="gamemode && fragmento.idTrabajoOrden && fragmento.operaciones && fragmento.operaciones.length"
            text color="primary" @click="abrirActualizarTc">
            <v-icon left small>mdi-timer-cog-outline</v-icon>{{ $t('Reports.ProductionPlan.detail.updateTc') }}
          </v-btn>
          <v-btn v-if="gamemode && fragmento.idTrabajoOrden" text color="error" @click="abrirFinalizarOdt">
            <v-icon left small>mdi-flag-checkered</v-icon>{{ $t('Reports.ProductionPlan.detail.finishOdt') }}
          </v-btn>
          <v-spacer />
          <v-btn text color="primary" @click="dialogFragmento = false">{{ $t('Reports.ProductionPlan.detail.close')
            }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: cambiar instancia (molde + instancia destino) de una ODT no iniciada -->
    <v-dialog v-model="dialogCambio" max-width="520" persistent>
      <v-card class="frag-card">
        <v-card-title class="frag-title">
          <v-icon left color="primary">mdi-swap-horizontal</v-icon>
          <span>{{ $t('Reports.ProductionPlan.changeInstance.title') }} · {{
            $t('Reports.ProductionPlan.detail.orderAbbr')
            }} {{ fragmento.clave }}</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-3">
          <div v-if="cambioCargando" class="cp-empty" style="padding:18px 0">{{
            $t('Reports.ProductionPlan.empty.loadingDestinations') }}</div>
          <template v-else>
            <div v-if="cambioError" class="cambio-error">{{ cambioError }}</div>
            <div v-if="!destinos.length" class="cp-empty" style="padding:18px 0">
              {{ $t('Reports.ProductionPlan.empty.noDestinations') }}
            </div>
            <template v-else>
              <label class="cambio-lbl">{{ $t('Reports.ProductionPlan.changeInstance.targetMold') }}</label>
              <v-select v-model="destinoMoldeSel" :items="opcionesMolde" item-text="text" item-value="value" dense
                outlined hide-details :placeholder="$t('Reports.ProductionPlan.changeInstance.selectMold')"
                @change="destinoInstanciaSel = null" />
              <label class="cambio-lbl">{{ $t('Reports.ProductionPlan.changeInstance.targetInstance') }}</label>
              <v-select v-model="destinoInstanciaSel" :items="opcionesInstancia" item-text="text" item-value="value"
                dense outlined hide-details :disabled="!destinoMoldeSel"
                :no-data-text="destinoMoldeSel ? $t('Reports.ProductionPlan.changeInstance.noInstances') : $t('Reports.ProductionPlan.changeInstance.chooseMoldFirst')"
                :placeholder="$t('Reports.ProductionPlan.changeInstance.selectInstance')" />
            </template>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogCambio = false" :disabled="cambioGuardando" color="success">{{
            $t('Reports.ProductionPlan.changeInstance.cancel') }}</v-btn>
          <v-btn color="primary" :loading="cambioGuardando" :disabled="!destinoMoldeSel || !destinoInstanciaSel"
            @click="confirmarCambioInstancia">{{ $t('Reports.ProductionPlan.changeInstance.moveOdt') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: finalizar (terminar anticipadamente) una ODT — solo gamemode -->
    <v-dialog v-model="dialogFinalizar" max-width="480" persistent>
      <v-card class="frag-card">
        <v-card-title class="frag-title">
          <v-icon left color="error">mdi-flag-checkered</v-icon>
          <span>{{ $t('Reports.ProductionPlan.finish.title') }} {{ fragmento.clave }}</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-3">
          <div v-if="finalizarError" class="cambio-error">{{ finalizarError }}</div>
          <p class="mb-0">
            {{ $t('Reports.ProductionPlan.finish.messageStart') }} <b>{{ fragmento.clave }}</b>
            ({{ fragmento.articulo }}). {{ $t('Reports.ProductionPlan.finish.messageEnd') }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogFinalizar = false" :disabled="finalizarGuardando" color="success">{{
            $t('Reports.ProductionPlan.finish.cancel') }}</v-btn>
          <v-btn color="error" :loading="finalizarGuardando" @click="confirmarFinalizarOdt">{{
            $t('Reports.ProductionPlan.finish.confirm') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: actualizar el TC por operación de la ODT — solo gamemode -->
    <v-dialog v-model="dialogTc" max-width="560" persistent>
      <v-card class="frag-card">
        <v-card-title class="frag-title">
          <v-icon left color="primary">mdi-timer-cog-outline</v-icon>
          <span>{{ $t('Reports.ProductionPlan.updateTc.title') }} {{ fragmento.clave }}</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-3">
          <div v-if="tcError" class="cambio-error">{{ tcError }}</div>
          <p class="mb-2 mt-0" style="color:#6b7682;font-size:12px">{{ $t('Reports.ProductionPlan.updateTc.help') }}</p>
          <table class="frag-ops">
            <thead>
              <tr>
                <th>{{ $t('Reports.ProductionPlan.detail.articleCode') }}</th>
                <th class="num">{{ $t('Reports.ProductionPlan.updateTc.currentTc') }}</th>
                <th class="num">{{ $t('Reports.ProductionPlan.updateTc.newTc') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(op, i) in tcOperaciones" :key="op.idOperacion || i">
                <td>{{ op.codigo || '—' }}</td>
                <td class="num">{{ op.tcActual ? op.tcActual + ' s' : '—' }}</td>
                <td class="num">
                  <v-text-field v-model.number="op.tc" type="number" min="1" dense outlined hide-details suffix="s"
                    style="max-width:120px;margin-left:auto" />
                </td>
              </tr>
            </tbody>
          </table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogTc = false" :disabled="tcGuardando" color="success">{{
            $t('Reports.ProductionPlan.updateTc.cancel') }}</v-btn>
          <v-btn color="primary" :loading="tcGuardando" :disabled="!tcValido" @click="confirmarActualizarTc">{{
            $t('Reports.ProductionPlan.updateTc.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: máquinas con actividad -->
    <v-dialog v-model="dialogActivas" max-width="460">
      <v-card class="frag-card">
        <v-card-title class="frag-title">
          <v-icon left color="primary">mdi-cog-outline</v-icon>
          <span>{{ $t('Reports.ProductionPlan.activeDialog.title') }}</span>
          <v-spacer />
          <span class="frag-banda" style="color:#2563d6">
            {{ maquinasActivas.activas }} / {{ maquinasActivas.total }}
          </span>
        </v-card-title>
        <v-divider />
        <v-tabs v-model="tabActivas" grow color="primary">
          <v-tab>{{ $t('Reports.ProductionPlan.activeDialog.tabActive') }} ({{ maquinasActivas.activas }})</v-tab>
          <v-tab>{{ $t('Reports.ProductionPlan.activeDialog.tabExpected') }} ({{ maquinasActivas.total }})</v-tab>
        </v-tabs>
        <v-divider />
        <v-card-text class="pt-3">
          <v-tabs-items v-model="tabActivas">
            <!-- Activas: máquinas que reportan actividad reciente -->
            <v-tab-item>
              <div v-if="instanciasActivas.length === 0" class="cp-empty" style="padding:18px 0">
                {{ $t('Reports.ProductionPlan.empty.noActiveMachines') }}
              </div>
              <div v-for="(it, i) in instanciasActivas" :key="'act-' + i" class="frag-row">
                <span class="act-name">
                  <i class="act-dot" :class="it.conOt ? 'act-dot--yes' : 'act-dot--no'" />
                  {{ it.maquina }}
                </span>
                <span class="act-right">
                  <span class="act-ot" :class="it.conOt ? 'act-ot--yes' : 'act-ot--no'">
                    {{ it.conOt ? $t('Reports.ProductionPlan.activeDialog.withOrder') :
                      $t('Reports.ProductionPlan.activeDialog.withoutOrder') }}
                  </span>
                  <b>{{ it.detalle }}</b>
                </span>
              </div>
            </v-tab-item>
            <!-- Esperadas: máquinas cuyo plan teórico cruza la línea de "ahora" -->
            <v-tab-item>
              <div v-if="maquinasEsperadas.length === 0" class="cp-empty" style="padding:18px 0">
                {{ $t('Reports.ProductionPlan.empty.noExpectedMachines') }}
              </div>
              <div v-for="(it, i) in maquinasEsperadas" :key="'esp-' + i" class="frag-row"
                :class="{ 'frag-row--arrancada': it.arrancada }">
                <span class="act-name">
                  <i v-if="it.arrancada" class="act-dot act-dot--yes" />
                  {{ it.maquina }}
                </span>
                <span class="act-right">
                  <span v-if="it.arrancada" class="act-ot act-ot--yes">
                    {{ $t('Reports.ProductionPlan.activeDialog.started') }}
                  </span>
                  <b>{{ it.detalle }}</b>
                </span>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="primary" @click="dialogActivas = false">{{ $t('Reports.ProductionPlan.detail.close')
            }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: configuración del reporte (eficiencia + bandas de color) — solo gamemode -->
    <v-dialog v-model="dialogConfig" max-width="600" persistent>
      <v-card class="frag-card">
        <v-card-title class="frag-title">
          <v-icon left color="orange darken-2">mdi-cog-outline</v-icon>
          <span>{{ $t('Reports.ProductionPlan.settings.title') }}</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-3">
          <label class="cambio-lbl">OT del periodo por</label>
          <v-select v-model="configReporte.filtroFecha" :items="filtroFechaOpciones" item-text="text"
            item-value="value" dense outlined hide-details style="max-width:300px" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            Con qué fecha se decide si una OT cae en el rango consultado. Por defecto,
            <b>fecha de entrega</b>. El filtro lo aplica el backend con esta misma configuración,
            así que también cambia el Excel y los reportes que se alimentan de este tablero.
            Con <b>fecha de entrega</b>, una OT sin fecha de inicio de producción entra al reporte
            pero no puede dibujar su barra teórica.
          </p>

          <v-divider class="my-4" />
          <label class="cambio-lbl">{{ $t('Reports.ProductionPlan.settings.percentBase') }}</label>
          <v-select v-model="configReporte.modoPorcentaje" :items="modoPorcentajeOpciones" item-text="text"
            item-value="value" dense outlined hide-details style="max-width:260px" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            {{ $t('Reports.ProductionPlan.settings.percentBaseHelp') }}
          </p>

          <v-divider class="my-4" />
          <label class="cambio-lbl">{{ $t('Reports.ProductionPlan.settings.fullscreenMode') }}</label>
          <v-select v-model="configReporte.modoPantallaCompleta" :items="modoPantallaCompletaOpciones" item-text="text"
            item-value="value" dense outlined hide-details style="max-width:260px" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            {{ $t('Reports.ProductionPlan.settings.fullscreenModeHelp') }}
          </p>

          <v-divider class="my-4" />
          <label class="cambio-lbl">{{ $t('Reports.ProductionPlan.settings.efficiency') }}</label>
          <v-text-field v-model.number="eficienciaPct" type="number" min="1" max="100" step="1" dense
            outlined hide-details suffix="%" style="max-width:160px" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            {{ $t('Reports.ProductionPlan.settings.efficiencyHelp') }}
          </p>

          <v-divider class="my-4" />
          <label class="cambio-lbl">Actualización automática</label>
          <v-text-field v-model.number="configReporte.intervaloSeg" type="number" :min="10" :max="3600" step="10" dense
            outlined hide-details suffix="seg" style="max-width:160px" />
          <p class="mt-2 mb-0" style="color:#6b7682;font-size:12px">
            Cada cuántos segundos se recarga el reporte automáticamente (entre 10 y 3600). Por defecto 180 (3 min).
          </p>

          <v-divider class="my-4" />
          <label class="cambio-lbl">Bandas de color por desfase</label>
          <p class="mt-0 mb-3" style="color:#6b7682;font-size:12px">
            Cada banda pinta las OT según su % de desfase. Toca el cuadro de color para cambiarlo;
            el límite inferior se calcula solo y la última banda cubre todo lo que supere su límite.
          </p>

          <div class="bandas-cards">
            <div v-for="(b, i) in configReporte.bandasDesfase" :key="i" class="banda-card">
              <div class="banda-card-top">
                <v-menu :close-on-content-click="false" offset-y>
                  <template #activator="{ on, attrs }">
                    <button type="button" class="banda-swatch" :style="{ background: b.color }" v-bind="attrs" v-on="on"
                      :title="b.color" />
                  </template>
                  <v-color-picker v-model="b.color" mode="hexa" hide-mode-switch flat />
                </v-menu>
                <span class="banda-rango-txt">{{ bandaRangoTexto(i) }}</span>
                <v-spacer />
                <v-btn icon small :disabled="configReporte.bandasDesfase.length <= 1" @click="quitarBanda(i)">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>
              <div class="banda-card-body">
                <div class="banda-field banda-field--grow">
                  <label>Nombre</label>
                  <v-text-field v-model="b.etiqueta" dense outlined hide-details placeholder="Nombre de la banda" />
                </div>
                <div v-if="i < configReporte.bandasDesfase.length - 1" class="banda-field">
                  <label>Cambia a los</label>
                  <v-text-field v-model.number="b.hasta" type="number" min="0" step="1" dense outlined hide-details
                    suffix="%" class="banda-hasta-input" />
                </div>
                <div v-else class="banda-ultima">
                  Última banda · cubre todo lo que supere {{ bandaDesde(i) }}%
                </div>
              </div>
            </div>
          </div>

          <v-btn text small color="primary" class="mt-2" @click="agregarBanda">
            <v-icon left small>mdi-plus</v-icon> Agregar banda
          </v-btn>
        </v-card-text>
        <v-card-actions>
          <v-btn text color="primary" :loading="configGuardando" @click="reestablecerConfig">
            {{ $t('Reports.ProductionPlan.settings.reset') }}
          </v-btn>
          <v-spacer />
          <v-btn text color="success" :disabled="configGuardando" @click="cerrarConfig">
            {{ $t('Reports.ProductionPlan.settings.cancel') }}
          </v-btn>
          <v-btn color="primary" :loading="configGuardando" @click="guardarConfig">
            {{ $t('Reports.ProductionPlan.settings.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import * as echarts from 'echarts'
import axios from 'axios'

// Bandas de color por desfase de tiempo (3 por defecto): verde 0–10%, amarillo 10–15%,
// rojo >15%. La última banda (hasta: null) es el catch-all. Debe coincidir con BandasDesfaseDefault
// del backend. Cada banda: { hasta: % límite superior inclusivo (null = "y más"), color hex, etiqueta }.
const getBandasDesfaseDefault = () => ([
  { hasta: 10, color: '#5b9bd5', etiqueta: '0%–10% (en meta)' },
  { hasta: 15, color: '#f0ac22', etiqueta: '10%–15% desfase' },
  { hasta: null, color: '#d64545', etiqueta: '>15% desfase' },
])

// Configuración persistida del reporte (vía ReportesConfig, código r100). La llave histórica
// porcentajeHolgura se conserva por compatibilidad, pero su valor representa la eficiencia.
const getDefaultConfigReporte = () => ({
  porcentajeHolgura: 85,
  bandasDesfase: getBandasDesfaseDefault(),
  // Intervalo de auto-actualización en segundos (default 3 min).
  intervaloSeg: 180,
  // Base del % de avance/progreso: 'semana' (objetivo total, como estaba) o 'hastaAhora'
  // (piezas programadas hasta ahora). Afecta piezas, %, color de barra y etiqueta secundaria.
  modoPorcentaje: 'semana',
  // Pantalla completa: 'compacta' (ajusta las filas para ver más máquinas) u 'original'
  // (alto fijo por fila, con scroll). Solo afecta la vista de pantalla completa.
  modoPantallaCompleta: 'compacta',
  // Fecha con la que el backend decide si una OT cae en el periodo: 'entrega' (default) o
  // 'inicio' (FechaInicioDeProduccion). El filtro lo aplica el back leyendo esta misma config,
  // así que también afecta al Excel y a los reportes que consumen el tablero.
  filtroFecha: 'entrega',
})

// Límites del intervalo de auto-actualización (segundos).
const INTERVALO_MIN_SEG = 10
const INTERVALO_MAX_SEG = 3600

const COLOR = {
  planFill: '#dcf0d6',
  planBorde: '#7cbd74',
  planTexto: '#2f6e2b',
  azul: '#2563d6',
  finJornada: '#1f78d1',
  ahora: '#c0392b',
  // Borde de ODT finalizada (algún detalle activo en estado 5).
  finalizada: '#22a84e',
}

const BANDA_COLOR = {
  // "En meta": azul claro (antes verde #63b46a).
  verde: '#5b9bd5',
  amarillo: '#f0ac22',
  naranja: '#e67e22',
  rojo: '#d64545',
}

const DIAS = {
  es: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
}
const MESES = {
  es: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
}

// Lee una propiedad aceptando camelCase o PascalCase.
const prop = (obj, camel) => {
  if (!obj) return undefined
  if (obj[camel] !== undefined) return obj[camel]
  const pascal = camel.charAt(0).toUpperCase() + camel.slice(1)
  return obj[pascal]
}

const toMs = (iso) => {
  if (!iso) return NaN
  const t = new Date(iso).getTime()
  return Number.isNaN(t) ? NaN : t
}

const colorBanda = (ok, objetivo, colorBackend) => {
  // El backend manda el color hex ya resuelto (banda de desfase configurable). Se respeta tal cual,
  // salvo el verde histórico de "en meta" (#63b46a), que se remapea al azul claro del indicador.
  if (typeof colorBackend === 'string' && colorBackend.charAt(0) === '#') {
    return colorBackend.toLowerCase() === '#63b46a' ? BANDA_COLOR.verde : colorBackend
  }
  if (colorBackend && BANDA_COLOR[colorBackend]) return BANDA_COLOR[colorBackend]
  if (!objetivo || objetivo <= 0) return BANDA_COLOR.verde
  const falt = Math.max(0, objetivo - ok)
  const pct = (falt * 100) / objetivo
  if (pct <= 1) return BANDA_COLOR.verde
  if (pct <= 10) return BANDA_COLOR.amarillo
  if (pct <= 15) return BANDA_COLOR.naranja
  return BANDA_COLOR.rojo
}

const fmtNum = (n) => Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })

const fmtHoras = (seg) => {
  const h = Math.round(Number(seg || 0) / 3600)
  return `${h} h`
}

const fmtFechaHora = (ms, locale = 'es') => {
  const d = new Date(ms)
  const p = (n) => String(n).padStart(2, '0')
  const dias = DIAS[locale] || DIAS.es
  const meses = MESES[locale] || MESES.es
  return `${dias[d.getDay()]} ${d.getDate()} ${meses[d.getMonth()]} ${p(d.getHours())}:${p(d.getMinutes())}`
}

const fmtDuracion = (seg) => {
  const s = Math.max(0, Math.round(Number(seg) || 0))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  if (h > 0) return `${h} h ${m} min`
  if (m > 0) return `${m} min`
  return `${s} s`
}

// Devuelve la CLAVE de la banda de desfase ('verde' | 'amarillo' | 'naranja' | 'rojo')
// para resolver el texto con i18n (reactivo al idioma), tanto en el template como en el tooltip.
const bandaKey = (ok, objetivo, colorBackend) => {
  const KEYS = ['verde', 'amarillo', 'naranja', 'rojo']
  if (colorBackend && KEYS.includes(colorBackend)) return colorBackend
  if (!objetivo || objetivo <= 0) return 'verde'
  const pct = (Math.max(0, objetivo - ok) * 100) / objetivo
  if (pct <= 1) return 'verde'
  if (pct <= 10) return 'amarillo'
  if (pct <= 15) return 'naranja'
  return 'rojo'
}

export default {
  name: 'ReportePlanDeProduccionV4',

  props: {
    active: { type: Boolean, default: false },
  },

  data() {
    return {
      chart: null,
      // Segundo gráfico: solo el eje de fechas, fijo (sticky) y sincronizado con el zoom del principal.
      ejeChart: null,
      // Alto de la cabecera fija (leyenda + avance), para anclar la barra de fechas justo debajo.
      stickyTopH: 96,
      resizeHandler: null,
      cargando: false,
      error: false,
      tablero: null,
      // Máquinas en producción según /ReportesMoldes/MaquinasActivas (tokens físicos
      // activos sin paros abiertos). Alimenta el KPI azul "Máquinas activas".
      maquinasActivasApi: [],
      // Loading del KPI/botón de máquinas activas mientras se consulta el endpoint.
      maquinasActivasCargando: false,
      vista: 'tiempoReal',
      filtros: { inicio: '', fin: '', idsArea: [], idsTrabajoOrden: [], idsInstancia: [] },
      dialogFragmento: false,
      dialogActivas: false,
      // Pestaña activa del diálogo de máquinas: 0 = activas, 1 = esperadas.
      tabActivas: 0,
      // Cambio de instancia (molde + instancia destino) para ODTs no iniciadas.
      dialogCambio: false,
      cambioCargando: false,
      cambioGuardando: false,
      cambioError: '',
      destinos: [],
      destinoMoldeSel: null,
      destinoInstanciaSel: null,
      // Finalizar (terminar anticipadamente) una ODT — solo gamemode.
      dialogFinalizar: false,
      finalizarGuardando: false,
      finalizarError: '',
      // Actualizar el TC por operación de la ODT — solo gamemode.
      dialogTc: false,
      tcGuardando: false,
      tcError: '',
      tcOperaciones: [],
      esFullscreen: false,
      // Alto disponible (px) para la gráfica en pantalla completa; se mide al entrar/redimensionar.
      fsAltoDisponible: 0,
      fsHandler: null,
      fragmento: {},
      // Configuración del reporte — solo gamemode.
      codigoReporte: 'r100',
      configReporte: getDefaultConfigReporte(),
      dialogConfig: false,
      configGuardando: false,
      // Auto-actualización: la primera carga usa la semana en curso; a partir de
      // ahí cada recarga (auto o manual) consulta con los filtros actuales.
      // El intervalo es configurable (configReporte.intervaloSeg).
      restante: 180,
      timerTick: null,
    }
  },

  computed: {
    gamemode() {
      return localStorage.getItem("gamemode") === "true";
    },

    // Intervalo de auto-actualización vigente (segundos), tomado de la config.
    intervaloSeg() {
      const n = Number(this.configReporte && this.configReporte.intervaloSeg)
      return Number.isFinite(n) && n > 0 ? n : 180
    },

    currentLocale() {
      return this.$i18n.locale
    },

    // Nombre de la planta a partir de la empresa activa (store.state.nombreEmpresa).
    // CAZEL-TLT se muestra como "Tultitlán" y Cazel como "Querétaro"; el resto usa el nombre tal cual.
    nombrePlanta() {
      const emp = String((this.$store && this.$store.state && this.$store.state.nombreEmpresa) || '').trim()
      const MAPA = { 'cazel-tlt': 'Tultitlán', 'cazel': 'Querétaro' }
      return MAPA[emp.toLowerCase()] || emp || '—'
    },

    maquinas() {
      return (this.tablero && (prop(this.tablero, 'maquinas') || [])) || []
    },

    maquinasFiltradas() {
      return this.maquinas
    },

    catAreas() {
      // El backend ya omite las áreas con Retrabajo = null (p. ej. "SIN DESIGNAR").
      return this.catalogoItems('areas')
    },

    // Opciones del filtro "Vista": tiempo real vs programado (teórico).
    vistaOpciones() {
      return [
        { text: this.$t('Reports.ProductionPlan.toolbar.realTime'), value: 'tiempoReal' },
        { text: this.$t('Reports.ProductionPlan.toolbar.theoretical'), value: 'teorico' },
      ]
    },

    // Eficiencia editable en la config (%). La llave porcentajeHolgura se mantiene únicamente
    // por compatibilidad con la configuración r100 existente.
    eficienciaPct: {
      get() {
        return Number(this.configReporte && this.configReporte.porcentajeHolgura) || 0
      },
      set(v) {
        let n = Number(v)
        if (!Number.isFinite(n)) n = 85
        n = Math.min(100, Math.max(1, n))
        this.configReporte.porcentajeHolgura = n
      },
    },

    // Opciones del modo de cálculo del % (config): semana vs hasta ahora.
    modoPorcentajeOpciones() {
      return [
        { text: this.$t('Reports.ProductionPlan.settings.percentWeek'), value: 'semana' },
        { text: this.$t('Reports.ProductionPlan.settings.percentSoFar'), value: 'hastaAhora' },
      ]
    },

    modoPantallaCompletaOpciones() {
      return [
        { text: this.$t('Reports.ProductionPlan.settings.fullscreenCompact'), value: 'compacta' },
        { text: this.$t('Reports.ProductionPlan.settings.fullscreenOriginal'), value: 'original' },
      ]
    },

    // Fecha con la que se decide si una OT entra al periodo consultado.
    filtroFechaOpciones() {
      return [
        { text: 'Fecha de entrega', value: 'entrega' },
        { text: 'Fecha de inicio de producción', value: 'inicio' },
      ]
    },

    catInstancias() {
      return this.catalogoItems('instancias')
    },

    catOrdenes() {
      return this.catalogoItems('ordenesTrabajo')
    },

    avance() {
      const a = (this.tablero && prop(this.tablero, 'avance')) || {}
      return {
        porcentaje: Number(prop(a, 'porcentaje') || 0),
        piezasOk: Number(prop(a, 'piezasOk') || 0),
        piezasObjetivo: Number(prop(a, 'piezasObjetivo') || 0),
        // Meta "hasta ahora" ya prorrateada por el backend (línea de fechaConsulta).
        piezasProgramadasHastaAhora: Number(prop(a, 'piezasProgramadasHastaAhora') || 0),
      }
    },

    // Piezas que el plan teórico esperaba producidas HASTA AHORA. La calcula el backend
    // (prorrateo por la línea de fechaConsulta) y la expone en avance.
    piezasProgramadasHastaAhora() {
      return this.avance.piezasProgramadasHastaAhora
    },

    // Modo del % de avance: true = "hasta ahora" (contra programadas hasta ahora),
    // false = "semana" (contra el objetivo total de la semana, como estaba).
    modoHastaAhora() {
      return (this.configReporte && this.configReporte.modoPorcentaje) === 'hastaAhora'
    },

    // Denominador del avance según el modo: programadas hasta ahora vs objetivo de la semana.
    denomAvance() {
      return this.modoHastaAhora ? this.piezasProgramadasHastaAhora : this.avance.piezasObjetivo
    },

    // % de avance sobre el denominador del modo (1 decimal, como el backend). La barra vive junto
    // a la fracción de la izquierda (contra el objetivo del periodo), así que ese es su
    // denominador; el modo solo decide el % que se anuncia.
    porcentajeAvance() {
      const d = this.denomAvance
      if (d <= 0) return 0
      return Math.round((this.avance.piezasOk / d) * 1000) / 10
    },

    // Desfase del avance global (%): cuánto falta respecto a lo que el plan esperaba HASTA AHORA.
    // SIEMPRE contra "programadas hasta ahora" (ritmo real), independientemente del modo del %:
    // así el color refleja si vamos al día, no la fracción de semana completada. 0 si al día/adelantados.
    desfaseAvancePct() {
      const prog = this.piezasProgramadasHastaAhora
      if (prog <= 0) return 0
      const falt = prog - this.avance.piezasOk
      return falt <= 0 ? 0 : (falt * 100) / prog
    },

    // Color de la barra de avance según el desfase, usando las MISMAS bandas configurables que las
    // OT (0%–10% en meta, 10%–15%, >15%). La última banda (hasta = null) es el catch-all.
    colorAvance() {
      const pct = this.desfaseAvancePct
      const bandas = this.bandasLeyenda
      for (let i = 0; i < bandas.length; i++) {
        const h = bandas[i].hasta
        if (h == null || pct <= Number(h)) return bandas[i].color
      }
      return bandas.length ? bandas[bandas.length - 1].color : '#d64545'
    },

    // Bandas para la leyenda: las que aplicó el backend (tablero.bandasDesfase); si no llegan,
    // las de la config local o las default.
    bandasLeyenda() {
      const delTablero = prop(this.tablero, 'bandasDesfase')
      const bandas = Array.isArray(delTablero) && delTablero.length
        ? delTablero
        : ((this.configReporte && this.configReporte.bandasDesfase) || getBandasDesfaseDefault())
      // "En meta" (primera banda, menor rango) siempre en azul claro, aunque el backend o la
      // config persistida traigan otro color. El resto de bandas se respetan tal cual.
      return bandas.map((b, i) => ({
        color: i === 0 ? BANDA_COLOR.verde : prop(b, 'color'),
        etiqueta: prop(b, 'etiqueta'),
        hasta: prop(b, 'hasta'),
      }))
    },

    altoGrafica() {
      // Alto reservado por máquina (fila). Menor = filas más juntas. Las barras se dibujan
      // como fracción de este alto (ver geometriaFila), así que también se ajustan solas.
      const n = this.maquinasFiltradas.length
      // En pantalla completa (modo "compacta"): llenar el alto disponible para que quepan TODAS las
      // máquinas sin scroll (las filas se comprimen solas). Piso por fila para no perder de vista los
      // datos: si hay tantas máquinas que no caben ni con el piso, se permite scroll legible.
      // En modo "original" se usa el alto fijo por fila de siempre (con scroll).
      const modoCompacto = !this.configReporte || this.configReporte.modoPantallaCompleta !== 'original'
      if (this.esFullscreen && modoCompacto && this.fsAltoDisponible > 0) {
        const MIN_FILA = 26
        return Math.max(n * MIN_FILA, this.fsAltoDisponible)
      }
      const ALTO_FILA = 44
      return Math.max(300, n * ALTO_FILA + 80)
    },

    // Ancho de la columna de nombres de máquina, ajustado al nombre más largo para que
    // ninguno se oculte pero sin dejar espacio muerto. Se usa como grid.left en ambos
    // gráficos (tablero y eje de fechas) para que sigan alineados.
    anchoColMaquina() {
      const et = (this.datos && this.datos.etiquetas) || []
      const maxLen = et.reduce((m, s) => Math.max(m, String(s || '').length), 0)
      // ~9.3px por carácter (fontSize 15, bold) + padding lateral.
      return Math.min(188, Math.max(52, Math.round(maxLen * 9.3) + 24))
    },

    // Todas las máquinas en producción que reporta /ReportesMoldes/MaquinasActivas, sin
    // restringir al tablero. Se ordenan según la posición de la máquina en la gráfica
    // (por nombre, sin distinción de mayúsculas/espacios) y las que no figuran en el
    // tablero quedan al final, pero ninguna se descarta.
    maquinasActivasFiltradas() {
      const orden = new Map()
      this.maquinasFiltradas.forEach((m, i) => {
        const nombre = String(prop(m, 'nombreMaquina') || '').trim().toUpperCase()
        if (nombre && !orden.has(nombre)) orden.set(nombre, i)
      })
      const pos = (m) => {
        const i = orden.get(String(prop(m, 'maquina') || '').trim().toUpperCase())
        return i === undefined ? Number.MAX_SAFE_INTEGER : i
      }
      return (this.maquinasActivasApi || []).slice().sort((a, b) => pos(a) - pos(b))
    },

    // KPI "Máquinas activas": activas = máquinas en producción (ya restringidas a los filtros);
    // total = instancias que, según el plan, deberían tener una ODT abierta ahora. Este
    // denominador lo calcula el backend (maquinasDebenProducir): máquinas cuyo plan teórico
    // cruza la línea de "ahora".
    maquinasActivas() {
      const total = Number(prop(this.tablero, 'maquinasDebenProducir') || 0)
      return { activas: this.maquinasActivasFiltradas.length, total }
    },

    // Resumen por área para la card "Maq. Activas": una columna por área del catálogo con
    // activas / esperadas. Activas = máquinas del endpoint MaquinasActivas agrupadas por idArea;
    // esperadas = máquinas cuya barra teórica NO finalizada cruza "ahora", agrupadas por el nombre
    // de área de esa barra. El catálogo ya viene sin "SIN DESIGNAR" (omitida en el backend).
    resumenPorArea() {
      const activasPorId = {}
        ; (this.maquinasActivasFiltradas || []).forEach((m) => {
          const id = prop(m, 'idArea')
          if (id == null) return
          activasPorId[id] = (activasPorId[id] || 0) + 1
        })

      const ahora = toMs(prop(this.tablero, 'fechaConsulta')) || Date.now()
      const esperadasPorNombre = {}
        ; (this.maquinasFiltradas || []).forEach((m) => {
          const cruza = (prop(m, 'barrasTeoricas') || []).find((b) => {
            if (prop(b, 'finalizada') === true) return false
            const ini = toMs(prop(b, 'inicio'))
            const fin = toMs(prop(b, 'fin'))
            return !Number.isNaN(ini) && !Number.isNaN(fin) && ahora >= ini && ahora < fin
          })
          if (!cruza) return
          const nombre = String(prop(cruza, 'area') || '').trim().toUpperCase()
          if (!nombre) return
          esperadasPorNombre[nombre] = (esperadasPorNombre[nombre] || 0) + 1
        })

      // Si hay áreas seleccionadas en el filtro, solo se muestran esas columnas; si no, todas.
      const sel = (this.filtros && this.filtros.idsArea) || []
      const areas = sel.length
        ? this.catAreas.filter((a) => sel.includes(a.value))
        : this.catAreas

      return areas.map((a) => ({
        id: a.value,
        nombre: a.text,
        activas: activasPorId[a.value] || 0,
        esperadas: esperadasPorNombre[String(a.text).trim().toUpperCase()] || 0,
      }))
    },

    // Nombres (normalizados) de máquinas que en el tablero tienen una OT arrancada: al menos una
    // barra real (producción registrada bajo una OT). Se cruza POR NOMBRE —la misma clave que usa
    // maquinasActivasFiltradas— porque es estable entre el endpoint de activas y el tablero
    // (el idMaquinaIns de las activas puede no venir resuelto).
    instanciasConOt() {
      const set = new Set()
      this.maquinasFiltradas.forEach((m) => {
        const tieneOt = (prop(m, 'barrasReales') || []).length > 0
        if (!tieneOt) return
        const nombre = String(prop(m, 'nombreMaquina') || prop(m, 'modeloMaquina') || '').trim().toUpperCase()
        if (nombre) set.add(nombre)
      })
      return set
    },

    // Detalle del KPI: máquinas en producción reportadas por el endpoint (ya filtradas), su
    // número de tokens (cavidades) activos y si tienen una OT arrancada (cruzando por nombre
    // contra las máquinas que el tablero muestra con barras reales).
    instanciasActivas() {
      const conOt = this.instanciasConOt
      return this.maquinasActivasFiltradas.map((m) => {
        const tokens = prop(m, 'tokens') || []
        const nombre = String(prop(m, 'maquina') || '').trim().toUpperCase()
        return {
          maquina: prop(m, 'maquina') || '—',
          detalle: `${tokens.length} ${this.$t('Reports.ProductionPlan.detail.cavities')}`,
          conOt: !!nombre && conOt.has(nombre),
        }
      })
    },

    // Máquinas que el plan espera produciendo "ahora": las que tienen un fragmento teórico
    // NO finalizado cuyo rango cruza la fecha de consulta (inicio <= ahora < fin). Una ODT
    // finalizada se descarta aunque su barra teórica siga cubriendo "ahora" (p. ej. una ODT
    // planificada para toda la semana que se cerró antes): ya no hay nada pendiente. Es el mismo
    // criterio que usa el backend para maquinasDebenProducir, por eso la cuenta coincide con el
    // denominador.
    maquinasEsperadas() {
      const ahora = toMs(prop(this.tablero, 'fechaConsulta')) || Date.now()
      const lista = []
      this.maquinasFiltradas.forEach((m) => {
        const enCurso = (prop(m, 'barrasTeoricas') || []).find((b) => {
          if (prop(b, 'finalizada') === true) return false
          const ini = toMs(prop(b, 'inicio'))
          const fin = toMs(prop(b, 'fin'))
          return !Number.isNaN(ini) && !Number.isNaN(fin) && ahora >= ini && ahora < fin
        })
        if (!enCurso) return
        lista.push({
          maquina: prop(m, 'nombreMaquina') || prop(m, 'modeloMaquina') || `#${prop(m, 'idMaquina')}`,
          detalle: prop(enCurso, 'clave') || prop(enCurso, 'molde') || '—',
          // La máquina ya arrancó la OT señalada: la barra teórica que cruza "ahora" está iniciada
          // (Iniciada = la ODT ya tiene producción real).
          arrancada: !!prop(enCurso, 'iniciada'),
        })
      })
      return lista
    },

    restanteTexto() {
      const s = Math.max(0, this.restante)
      const m = Math.floor(s / 60)
      return `${m}:${String(s % 60).padStart(2, '0')}`
    },

    // Opciones del selector de molde destino (cambio de instancia).
    opcionesMolde() {
      return (this.destinos || []).map((d) => ({
        value: prop(d, 'idMolde'),
        text: `${prop(d, 'codigoMolde') || 'Molde'} · ${prop(d, 'modeloMaquina') || ''}`.trim(),
      }))
    },

    // Instancias del molde destino seleccionado.
    opcionesInstancia() {
      const d = (this.destinos || []).find((x) => prop(x, 'idMolde') === this.destinoMoldeSel)
      if (!d) return []
      return (prop(d, 'instancias') || []).map((i) => {
        const tk = Number(prop(i, 'tokensFisicos') || 0)
        const nombre = prop(i, 'nombre') || `#${prop(i, 'idMaquinaIns')}`
        return {
          value: prop(i, 'idMaquinaIns'),
          // text: tk > 0 ? `${nombre} · ${tk} token(s)` : nombre,
          text: tk > 0 ? `${nombre}` : nombre,
        }
      })
    },

    // Habilita el guardado del nuevo TC: todas las filas con un TC entero positivo.
    tcValido() {
      return this.tcOperaciones.length > 0 &&
        this.tcOperaciones.every((o) => Number(o.tc) > 0)
    },

    // Etiqueta del tipo de fragmento (real ajustado vs plan teórico), reactiva al idioma.
    tipoLabel() {
      if (this.fragmento.esReal == null) return ''
      return this.fragmento.esReal
        ? this.$t('Reports.ProductionPlan.detail.realAdjusted')
        : this.$t('Reports.ProductionPlan.detail.theoreticalPlan')
    },

    // Texto de la banda de desfase. Usa la etiqueta configurable que manda el backend;
    // si no hay (datos antiguos), cae al texto i18n por clave.
    bandaLabel() {
      if (this.fragmento.bandaEtiqueta) return this.fragmento.bandaEtiqueta
      const map = { verde: 'onTarget', amarillo: 'off1to10', naranja: 'off10to15', rojo: 'offOver15' }
      const k = map[this.fragmento.bandaKey]
      return k ? this.$t('Reports.ProductionPlan.band.' + k) : ''
    },

    // Desfase de TIEMPO (real vs teórico) en porcentaje, calculado por el backend.
    desfasePctTexto() {
      const pct = Number(this.fragmento.desfasePct || 0)
      return pct <= 0 ? this.$t('Reports.ProductionPlan.band.onTarget') : `${pct.toFixed(1)}%`
    },

    // Desfase de TIEMPO expresado como duración (cuánto tardó de más respecto al plan).
    desfaseTiempo() {
      const seg = Number(this.fragmento.desfaseSeg || 0)
      return seg <= 0 ? this.$t('Reports.ProductionPlan.band.onTarget') : fmtDuracion(seg)
    },

    datos() {
      const maquinas = this.maquinasFiltradas
      const etiquetas = maquinas.map((m) => prop(m, 'nombreMaquina') || prop(m, 'modeloMaquina') || `#${prop(m, 'idMaquina')}`)

      const meta = {}
      maquinas.forEach((m, i) => {
        meta[etiquetas[i]] = {
          molde: prop(m, 'molde'),
          avanceSeg: Number(prop(m, 'segundosAvance') || 0),
          faltanteSeg: Number(prop(m, 'segundosFaltante') || 0),
          tcProgramado: prop(m, 'tcProgramado'),
          tcReal: prop(m, 'tcReal'),
          tcEsperado: prop(m, 'tcEsperado'),
        }
      })

      const plan = []
      const real = []
      let minMs = Infinity
      let maxMs = -Infinity

      maquinas.forEach((m, idx) => {
        const barrasPlan = prop(m, 'barrasTeoricas') || []
        const barrasReal = prop(m, 'barrasReales') || []

        barrasPlan.forEach((b) => {
          const ini = toMs(prop(b, 'inicio'))
          const fin = toMs(prop(b, 'fin'))
          if (Number.isNaN(ini) || Number.isNaN(fin) || fin <= ini) return
          minMs = Math.min(minMs, ini)
          maxMs = Math.max(maxMs, fin)
          plan.push({
            value: [
              idx,
              ini,
              fin,
              prop(b, 'clave') || '',
              Number(prop(b, 'piezasObjetivo') || 0),
              prop(b, 'molde') || '',
              prop(b, 'otCritica') ? 1 : 0,
            ],
            raw: b,
          })
        })

        if (this.vista === 'tiempoReal') {
          barrasReal.forEach((b) => {
            const ini = toMs(prop(b, 'inicio'))
            const fin = toMs(prop(b, 'fin'))
            if (Number.isNaN(ini) || Number.isNaN(fin) || fin <= ini) return
            minMs = Math.min(minMs, ini)
            maxMs = Math.max(maxMs, fin)
            const ok = Number(prop(b, 'piezasOk') || 0)
            const obj = Number(prop(b, 'piezasObjetivo') || 0)
            real.push({
              value: [
                idx,
                ini,
                fin,
                prop(b, 'clave') || '',
                ok,
                obj,
                colorBanda(ok, obj, prop(b, 'color')),
                prop(b, 'enCurso') ? 1 : 0,
                prop(b, 'molde') || '',
                prop(b, 'finalizada') ? 1 : 0,
              ],
              raw: b,
            })
          })
        }
      })

      if (!Number.isFinite(minMs) || !Number.isFinite(maxMs)) {
        const ahora = Date.now()
        minMs = ahora - 12 * 3600 * 1000
        maxMs = ahora + 12 * 3600 * 1000
      }

      const margen = 60 * 60 * 1000
      return {
        etiquetas,
        meta,
        plan,
        real,
        min: minMs - margen,
        max: maxMs + margen,
        finJornada: toMs(prop(this.tablero, 'fechaFinJornada')),
      }
    },
  },

  watch: {
    active(val) {
      if (val) {
        if (!this.tablero) this.cargar()
        else this.$nextTick(() => this.render())
        this.iniciarAutoActualizacion()
      } else {
        this.detenerAutoActualizacion()
      }
    },
    vista() {
      this.render()
    },
    // Al cambiar de idioma, el canvas de echarts no es reactivo: hay que repintarlo
    // para que días/meses y las etiquetas "Ahora"/"Fin Jornada" tomen el nuevo locale.
    currentLocale() {
      this.render()
    },
    // Al cambiar el alto de la gráfica (p. ej. al entrar/salir de pantalla completa) echarts
    // no reacciona solo: hay que redimensionarlo y re-anclar la barra de fechas.
    altoGrafica() {
      this.$nextTick(() => {
        if (this.chart) this.chart.resize()
        if (this.ejeChart) this.ejeChart.resize()
        this.medirStickyTop()
        this.sincronizarEjeFecha()
      })
    },
  },

  async mounted() {
    this.resizeHandler = () => {
      this.medirAltoGrafica()
      if (this.chart) this.chart.resize()
      if (this.ejeChart) this.ejeChart.resize()
      this.medirStickyTop()
      this.sincronizarEjeFecha()
    }
    window.addEventListener('resize', this.resizeHandler)
    this.fsHandler = () => this.syncFullscreen()
    document.addEventListener('fullscreenchange', this.fsHandler)
    document.addEventListener('webkitfullscreenchange', this.fsHandler)
    // Ajusta el rango por defecto con los horarios activos antes de la primera carga.
    await this.inicializarDesdeHorarios()
    if (this.active || !this.tablero) this.cargar()
    if (this.active) this.iniciarAutoActualizacion()
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.resizeHandler)
    document.removeEventListener('fullscreenchange', this.fsHandler)
    document.removeEventListener('webkitfullscreenchange', this.fsHandler)
    this.detenerAutoActualizacion()
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
    if (this.ejeChart) {
      this.ejeChart.dispose()
      this.ejeChart = null
    }
  },

  methods: {
    fmtNum,

    // Pantalla completa de todo el reporte (la tarjeta raíz).
    toggleFullscreen() {
      const root = this.$refs.fullscreenRoot
      const el = root && root.$el ? root.$el : root
      if (!el) return
      const doc = document
      const enFs = doc.fullscreenElement || doc.webkitFullscreenElement
      if (!enFs) {
        if (el.requestFullscreen) el.requestFullscreen()
        else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen()
      } else {
        if (doc.exitFullscreen) doc.exitFullscreen()
        else if (doc.webkitExitFullscreen) doc.webkitExitFullscreen()
      }
    },

    syncFullscreen() {
      const doc = document
      const fsEl = doc.fullscreenElement || doc.webkitFullscreenElement
      const root = this.$refs.fullscreenRoot
      const el = root && root.$el ? root.$el : root
      this.esFullscreen = !!fsEl && fsEl === el
      // El cambio de pantalla completa no dispara resize de window: re-ajustar la gráfica.
      // Además la cabecera cambia de alto (se ocultan filtros/selector y se compacta),
      // así que re-medimos su altura y re-anclamos la barra de fechas.
      this.$nextTick(() => {
        this.medirAltoGrafica()
        this.$nextTick(() => {
          if (this.chart) this.chart.resize()
          if (this.ejeChart) this.ejeChart.resize()
          this.medirStickyTop()
          this.sincronizarEjeFecha()
        })
      })
    },

    // Mide el alto disponible bajo la gráfica en pantalla completa (viewport menos lo que hay
    // arriba: cabecera compacta, cards, leyenda). Con esto la gráfica llena la pantalla y las
    // filas se ajustan para que quepan más máquinas.
    medirAltoGrafica() {
      if (!this.esFullscreen) { this.fsAltoDisponible = 0; return }
      const el = this.$refs.grafica
      if (!el) return
      const top = el.getBoundingClientRect().top
      this.fsAltoDisponible = Math.max(200, Math.round(window.innerHeight - top - 8))
    },

    // Lunes (00:00) de la semana en curso.
    lunesSemanaActual() {
      const hoy = new Date()
      const diff = (hoy.getDay() + 6) % 7 // lunes = 0
      const lunes = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate())
      lunes.setDate(lunes.getDate() - diff)
      return lunes
    },

    // Formatea una fecha al formato del picker: "YYYY-MM-DD HH:mm".
    fmtFiltro(d) {
      const p = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
    },

    // Convierte el valor del picker ("YYYY-MM-DD HH:mm" o "YYYY-MM-DD") a ISO para el query.
    filtroAIso(v) {
      if (!v) return v
      const s = String(v).trim().replace(' ', 'T')
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s}T00:00:00`
      if (/T\d{2}:\d{2}$/.test(s)) return `${s}:00`
      return s
    },

    // Fallback síncrono: semana en curso (lunes 00:00 → lunes siguiente 00:00).
    inicializarSemana() {
      const inicio = this.lunesSemanaActual()
      const fin = new Date(inicio)
      fin.setDate(inicio.getDate() + 7)
      this.filtros.inicio = this.fmtFiltro(inicio)
      this.filtros.fin = this.fmtFiltro(fin)
    },

    // Calcula el rango por defecto a partir de los horarios activos (CAT_Horarios):
    // toma el día con menor diaSemana (0=domingo … 6=sábado) y su hora de inicio,
    // ubicándolo en su última ocurrencia en o antes de hoy; el fin es ese mismo
    // punto una semana después.
    async inicializarDesdeHorarios() {
      try {
        // /Horarios responde { events, start, intervals }: los horarios están en events.
        const { data } = await axios.get('/Horarios')
        const horarios = Array.isArray(data)
          ? data
          : Array.isArray(data && data.events)
            ? data.events
            : []
        if (!horarios.length) {
          this.inicializarSemana()
          return
        }

        const menor = horarios.reduce((min, h) =>
          Number(prop(h, 'diaSemana')) < Number(prop(min, 'diaSemana')) ? h : min,
        )
        const diaSemana = Number(prop(menor, 'diaSemana'))
        const [hh, mm] = String(prop(menor, 'inicio') || '00:00').split(':').map(Number)

        // Última ocurrencia de ese día de la semana en o antes de hoy
        // (0=domingo … 6=sábado), p. ej. domingo → el último domingo desde hoy.
        const hoy = new Date()
        const inicio = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate())
        const diff = (inicio.getDay() - diaSemana + 7) % 7
        inicio.setDate(inicio.getDate() - diff)
        inicio.setHours(hh || 0, mm || 0, 0, 0)

        const fin = new Date(inicio)
        fin.setDate(inicio.getDate() + 7)

        this.filtros.inicio = this.fmtFiltro(inicio)
        this.filtros.fin = this.fmtFiltro(fin)
      } catch (e) {
        // Si falla, se usa el fallback de la semana en curso.
        this.inicializarSemana()
        // eslint-disable-next-line no-console
        console.error('Error cargando horarios para fechas por defecto', e)
      }
    },

    // Convierte el catálogo del backend (camel/Pascal) a items {text, value} para v-autocomplete.
    catalogoItems(clave) {
      const cat = prop(this.tablero, 'catalogos') || {}
      const lista = prop(cat, clave) || []
      return lista.map((it) => ({ text: prop(it, 'nombre'), value: prop(it, 'id') }))
    },

    // Reinicia la cuenta regresiva a su valor completo (5 min).
    reiniciarContador() {
      this.restante = this.intervaloSeg
    },

    // Tick de 1 s: descuenta el contador y dispara la recarga al llegar a 0.
    // No descuenta mientras hay una carga en curso para no encimar peticiones.
    iniciarAutoActualizacion() {
      this.detenerAutoActualizacion()
      this.reiniciarContador()
      this.timerTick = setInterval(() => {
        if (this.cargando) return
        if (this.restante <= 1) this.cargar()
        else this.restante -= 1
      }, 1000)
    },

    detenerAutoActualizacion() {
      if (this.timerTick) {
        clearInterval(this.timerTick)
        this.timerTick = null
      }
    },

    async cargar() {
      // Toda recarga (manual o automática) reinicia el contador.
      this.reiniciarContador()
      // Cada recarga refresca las preferencias del reporte (las crea si no existen).
      this.cargarPreferencias()
      this.cargando = true
      this.error = false
      try {
        const p = new URLSearchParams()
        if (this.filtros.inicio) p.append('inicio', this.filtroAIso(this.filtros.inicio))
        if (this.filtros.fin) p.append('fin', this.filtroAIso(this.filtros.fin))
          ; (this.filtros.idsArea || []).forEach((v) => p.append('idsArea', v))
          ; (this.filtros.idsTrabajoOrden || []).forEach((v) => p.append('idsTrabajoOrden', v))
          ; (this.filtros.idsInstancia || []).forEach((v) => p.append('idsInstancia', v))

        const { data } = await axios.get(`/ReportesMoldes/TableroProduccionV4?${p.toString()}`)
        this.tablero = data
        this.$nextTick(() => this.render())
      } catch (e) {
        this.error = true
        // eslint-disable-next-line no-console
        console.error('Error cargando tablero de producción', e)
      } finally {
        this.cargando = false
      }
      // Las máquinas activas son tiempo real (no dependen de los filtros del tablero).
      this.cargarMaquinasActivas()
    },

    // Carga las máquinas en producción (KPI azul). Aplica el mismo filtro de área que el
    // tablero (resuelto en backend vía token → instancia → área). Si falla, se conserva el
    // último valor conocido sin marcar el tablero como erróneo.
    async cargarMaquinasActivas() {
      this.maquinasActivasCargando = true
      try {
        const p = new URLSearchParams()
          ; (this.filtros.idsArea || []).forEach((v) => p.append('idsArea', v))
        const qs = p.toString()
        const { data } = await axios.get(`/ReportesMoldes/MaquinasActivas${qs ? `?${qs}` : ''}`)
        this.maquinasActivasApi = Array.isArray(data) ? data : []
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error cargando máquinas activas', e)
      } finally {
        this.maquinasActivasCargando = false
      }
    },

    // Abre el selector de cambio de instancia y carga los moldes/instancias destino válidos.
    async abrirCambioInstancia() {
      this.cambioError = ''
      this.destinos = []
      this.destinoMoldeSel = null
      this.destinoInstanciaSel = null
      this.dialogCambio = true
      this.cambioCargando = true
      try {
        const { data } = await axios.get(
          `/TrabajoOrdenes/DestinosCambioInstancia/${this.fragmento.idTrabajoOrden}`,
        )
        this.destinos = Array.isArray(data) ? data : []
      } catch (e) {
        this.cambioError = this.$t('Reports.ProductionPlan.errors.loadDestinations')
        // eslint-disable-next-line no-console
        console.error('Error cargando destinos de cambio de instancia', e)
      } finally {
        this.cambioCargando = false
      }
    },

    // Ejecuta el cambio (PUT) y recarga el tablero para reflejar la nueva instancia.
    async confirmarCambioInstancia() {
      if (!this.fragmento.idTrabajoOrden || !this.destinoMoldeSel || !this.destinoInstanciaSel) return
      this.cambioGuardando = true
      this.cambioError = ''
      try {
        await axios.put(
          `/TrabajoOrdenes/CambiarRecetasInicialesOTPorMolde/${this.fragmento.idTrabajoOrden}/${this.destinoMoldeSel}`,
          null,
          { params: { idMaquinaIns: this.destinoInstanciaSel } },
        )
        this.dialogCambio = false
        this.dialogFragmento = false
        await this.cargar()
      } catch (e) {
        const status = e && e.response && e.response.status
        this.cambioError =
          status === 409
            ? this.$t('Reports.ProductionPlan.errors.moveConflict')
            : this.$t('Reports.ProductionPlan.errors.moveFailed')
        // eslint-disable-next-line no-console
        console.error('Error en cambio de instancia', e)
      } finally {
        this.cambioGuardando = false
      }
    },

    // Abre el diálogo de confirmación para terminar anticipadamente la ODT.
    abrirFinalizarOdt() {
      this.finalizarError = ''
      this.dialogFinalizar = true
    },

    // Termina anticipadamente la ODT (PUT) y recarga el tablero.
    async confirmarFinalizarOdt() {
      if (!this.fragmento.idTrabajoOrden) return
      this.finalizarGuardando = true
      this.finalizarError = ''
      try {
        await axios.put(
          `/TrabajoOrdenes/TerminarAnticipadamenteOT/${this.fragmento.idTrabajoOrden}`,
          {},
        )
        this.dialogFinalizar = false
        this.dialogFragmento = false
        await this.cargar()
      } catch (e) {
        this.finalizarError = this.$t('Reports.ProductionPlan.errors.finishFailed')
        // eslint-disable-next-line no-console
        console.error('Error finalizando ODT anticipadamente', e)
      } finally {
        this.finalizarGuardando = false
      }
    },

    // Abre el diálogo para actualizar el TC, precargando el TC actual de cada operación.
    abrirActualizarTc() {
      this.tcError = ''
      this.tcOperaciones = (this.fragmento.operaciones || []).map((o) => ({
        idOperacion: o.idOperacion,
        codigo: o.codigo,
        tcActual: Number(o.tc || 0),
        tc: Number(o.tc || 0),
      }))
      this.dialogTc = true
    },

    // Envía el nuevo TC por operación (PUT) y recarga el tablero. El backend aplica el TC
    // igual que OtsServiceV4 (TiempoMin/Max y TiempoTotalMin/Max).
    async confirmarActualizarTc() {
      if (!this.fragmento.idTrabajoOrden || !this.tcValido) return
      this.tcGuardando = true
      this.tcError = ''
      try {
        const payload = this.tcOperaciones
          .filter((o) => o.idOperacion && Number(o.tc) > 0)
          .map((o) => ({ idOperacion: o.idOperacion, tc: Math.round(Number(o.tc)) }))
        await axios.put(
          `/TrabajoOrdenes/ActualizarTiemposCicloOT/${this.fragmento.idTrabajoOrden}`,
          payload,
        )
        this.dialogTc = false
        this.dialogFragmento = false
        await this.cargar()
      } catch (e) {
        this.tcError = this.$t('Reports.ProductionPlan.errors.updateTcFailed')
        // eslint-disable-next-line no-console
        console.error('Error actualizando TC de la ODT', e)
      } finally {
        this.tcGuardando = false
      }
    },

    // ===================== CONFIGURACIÓN DEL REPORTE (eficiencia + bandas) =====================
    // Normaliza el objeto de configuración, asegurando tipos y rellenando faltantes.
    normalizarConfig(cfg = {}) {
      const source = cfg && typeof cfg === 'object' ? cfg : {}
      const def = getDefaultConfigReporte()
      let eficiencia = Number(source.porcentajeHolgura)
      if (!Number.isFinite(eficiencia)) eficiencia = def.porcentajeHolgura
      eficiencia = Math.min(100, Math.max(1, eficiencia))
      let intervalo = Number(source.intervaloSeg)
      if (!Number.isFinite(intervalo)) intervalo = def.intervaloSeg
      intervalo = Math.round(Math.min(INTERVALO_MAX_SEG, Math.max(INTERVALO_MIN_SEG, intervalo)))
      const modoPorcentaje = source.modoPorcentaje === 'hastaAhora' ? 'hastaAhora' : 'semana'
      const modoPantallaCompleta = source.modoPantallaCompleta === 'original' ? 'original' : 'compacta'
      // Cualquier valor distinto de 'inicio' cae en 'entrega', que es el default.
      const filtroFecha = source.filtroFecha === 'inicio' ? 'inicio' : 'entrega'
      return {
        porcentajeHolgura: eficiencia,
        bandasDesfase: this.normalizarBandas(source.bandasDesfase),
        intervaloSeg: intervalo,
        modoPorcentaje,
        modoPantallaCompleta,
        filtroFecha,
      }
    },

    // Normaliza la lista de bandas de desfase: cada banda { hasta (número|null), color (#hex),
    // etiqueta }. Descarta entradas sin color, ordena ascendente por "hasta" (null al final) y
    // fuerza la última banda como catch-all (hasta = null). Si no hay bandas válidas, usa el default.
    normalizarBandas(bandas) {
      const lista = Array.isArray(bandas) ? bandas : []
      const limpias = lista
        .filter((b) => b && typeof b === 'object' && typeof b.color === 'string' && b.color.trim())
        .map((b) => {
          // OJO: Number(null) y Number('') devuelven 0 (no NaN), así que hay que tratar
          // null/undefined/'' como catch-all (hasta = null) ANTES de convertir a número.
          let hasta = null
          if (b.hasta !== null && b.hasta !== undefined && b.hasta !== '') {
            const n = Number(b.hasta)
            hasta = Number.isFinite(n) ? n : null
          }
          let color = String(b.color).trim()
          // El color-picker (modo hexa) puede devolver 8 dígitos (#rrggbbaa); echarts espera 6.
          if (/^#[0-9a-fA-F]{8}$/.test(color)) color = color.slice(0, 7)
          return {
            hasta,
            color,
            etiqueta: typeof b.etiqueta === 'string' ? b.etiqueta : '',
          }
        })
      if (!limpias.length) return getBandasDesfaseDefault()
      limpias.sort((a, b) => {
        if (a.hasta == null) return 1
        if (b.hasta == null) return -1
        return a.hasta - b.hasta
      })
      // La última banda siempre es el catch-all (sin límite superior).
      limpias[limpias.length - 1].hasta = null
      return limpias
    },

    // Desempaqueta la preferencia: puede venir como string JSON, como { configJson } (camel
    // o Pascal) o como el objeto de configuración directo.
    extraerConfig(pref = null) {
      if (!pref) return {}
      if (typeof pref === 'string') {
        try { return JSON.parse(pref) } catch (e) { return {} }
      }
      if (typeof pref !== 'object') return {}

      const rawConfigJson = pref.configJson ?? pref.ConfigJson
      if (rawConfigJson != null) {
        if (typeof rawConfigJson === 'string') {
          try { return JSON.parse(rawConfigJson) } catch (e) { return {} }
        }
        if (typeof rawConfigJson === 'object') return rawConfigJson
      }
      return pref
    },

    guardarConfigLocal(cfg) {
      const normalizada = this.normalizarConfig(this.extraerConfig(cfg))
      localStorage.setItem(this.codigoReporte, JSON.stringify(normalizada))
    },

    recuperarConfigLocal() {
      try {
        const data = localStorage.getItem(this.codigoReporte)
        if (!data) return this.normalizarConfig()
        return this.normalizarConfig(this.extraerConfig(JSON.parse(data)))
      } catch (e) {
        return this.normalizarConfig()
      }
    },

    // Carga las preferencias desde el backend. Si no existen (404), crea las default y las
    // persiste. Se invoca en cada recarga del reporte; mientras el diálogo está abierto no
    // sobrescribe el estado para no pisar lo que el usuario está editando.
    async cargarPreferencias() {
      if (this.dialogConfig) return
      try {
        const { data } = await axios.get(`ReportesConfig/Obtener/${this.codigoReporte}`)
        const pref = this.normalizarConfig(this.extraerConfig(data))
        this.configReporte = { ...pref }
        this.guardarConfigLocal(pref)
      } catch (e) {
        const def = this.normalizarConfig()
        this.configReporte = { ...def }
        this.guardarConfigLocal(def)
        // No existían preferencias: las creamos con el default.
        this.persistirPreferencias(def)
      }
    },

    // POST de la configuración al backend (ReportesConfig/Guardar).
    async persistirPreferencias(cfg) {
      const pref = this.normalizarConfig(this.extraerConfig(cfg))
      this.configGuardando = true
      try {
        await axios.post('ReportesConfig/Guardar/', {
          codigoReporte: this.codigoReporte,
          configJson: JSON.stringify(pref),
        })
        this.configReporte = { ...pref }
        this.guardarConfigLocal(pref)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error guardando configuración del reporte', e)
      } finally {
        this.configGuardando = false
      }
    },

    // Texto de rango para la leyenda cuando la banda no tiene etiqueta propia.
    rangoBandaLeyenda(i) {
      const bandas = this.bandasLeyenda
      const desde = i === 0 ? 0 : (Number(bandas[i - 1].hasta) || 0)
      const hasta = bandas[i].hasta
      return hasta == null ? `>${desde}% desfase` : `${desde}%–${hasta}% desfase`
    },

    // Texto legible del rango de la banda i para el encabezado de su tarjeta.
    bandaRangoTexto(i) {
      const bandas = this.configReporte.bandasDesfase
      const desde = this.bandaDesde(i)
      const b = bandas[i]
      if (i === bandas.length - 1 || b.hasta == null || b.hasta === '') {
        return `Desfase de ${desde}% en adelante`
      }
      return `Desfase de ${desde}% a ${b.hasta}%`
    },

    // Límite inferior (en %) de la banda i: 0 para la primera, el "hasta" de la anterior para el resto.
    bandaDesde(i) {
      if (i <= 0) return 0
      const prev = this.configReporte.bandasDesfase[i - 1]
      const h = Number(prev && prev.hasta)
      return Number.isFinite(h) ? h : 0
    },

    // Agrega una banda nueva (catch-all) y convierte la anterior última en banda con límite.
    agregarBanda() {
      const bandas = this.configReporte.bandasDesfase
      const ult = bandas[bandas.length - 1]
      const prevHasta = bandas.length >= 2 ? Number(bandas[bandas.length - 2].hasta) : 0
      // La que era catch-all toma un límite (su 'desde' + 5) para dejar hueco a la nueva.
      if (ult) ult.hasta = (Number.isFinite(prevHasta) ? prevHasta : 0) + 5
      bandas.push({ hasta: null, color: '#d64545', etiqueta: '' })
    },

    quitarBanda(i) {
      const bandas = this.configReporte.bandasDesfase
      if (bandas.length <= 1) return
      bandas.splice(i, 1)
      // La última banda siempre es catch-all (sin límite superior).
      bandas[bandas.length - 1].hasta = null
    },

    abrirConfig() {
      this.configReporte = { ...this.recuperarConfigLocal() }
      this.dialogConfig = true
    },

    cerrarConfig() {
      this.configReporte = { ...this.recuperarConfigLocal() }
      this.dialogConfig = false
    },

    async guardarConfig() {
      const filtroPrevio = (this.recuperarConfigLocal() || {}).filtroFecha
      const filtroNuevo = (this.configReporte || {}).filtroFecha
      await this.persistirPreferencias(this.configReporte)
      // El nuevo intervalo se aplica de inmediato reiniciando la cuenta regresiva.
      if (this.active && this.timerTick) this.reiniciarContador()
      if (!this.configGuardando) this.dialogConfig = false
      // La fecha del filtro la lee el backend al consultar: cambiarla cambia el conjunto de OT,
      // así que hay que volver a pedir el tablero.
      if (filtroNuevo !== filtroPrevio) await this.cargar()
    },

    async reestablecerConfig() {
      const filtroPrevio = (this.recuperarConfigLocal() || {}).filtroFecha
      const def = getDefaultConfigReporte()
      await this.persistirPreferencias(def)
      if (def.filtroFecha !== filtroPrevio) await this.cargar()
    },

    render() {
      if (!this.tablero) return
      const el = this.$refs.grafica
      if (!el) return
      if (!this.chart) this.chart = echarts.init(el)
      this.chart.setOption(this.crearOption(this.datos), true)
      this.chart.off('click')
      this.chart.on('click', this.onClickFragmento)
      // Sincroniza la barra de fechas fija con el zoom/paneo del gráfico principal.
      this.chart.off('dataZoom')
      this.chart.on('dataZoom', this.sincronizarEjeFecha)
      this.renderEjeFecha()
      this.$nextTick(() => {
        if (this.chart) this.chart.resize()
        this.medirStickyTop()
        this.sincronizarEjeFecha()
      })
    },

    // Barra de fechas: segundo gráfico con SOLO el eje de tiempo. Usa los mismos offsets de grid
    // (left/right) y vive en la misma caja que el gráfico, por lo que las fechas quedan alineadas
    // con las líneas verticales del tablero.
    renderEjeFecha() {
      const el = this.$refs.ejeFecha
      if (!el || this.maquinasFiltradas.length === 0) return
      if (!this.ejeChart) this.ejeChart = echarts.init(el)
      this.ejeChart.setOption(this.crearOptionEje(this.datos), true)
      this.$nextTick(() => this.ejeChart && this.ejeChart.resize())
    },

    // Copia al eje fijo la ventana de tiempo visible del gráfico principal, para que el zoom/paneo
    // se refleje en las fechas ancladas.
    sincronizarEjeFecha() {
      if (!this.ejeChart || !this.chart) return
      let ext = null
      try {
        ext = this.chart.getModel().getComponent('xAxis', 0).axis.scale.getExtent()
      } catch (e) {
        ext = null
      }
      if (ext && ext.length === 2 && isFinite(ext[0]) && isFinite(ext[1]) && ext[1] > ext[0]) {
        this.ejeChart.setOption({ xAxis: { min: ext[0], max: ext[1] } })
      }
    },

    // Mide la altura real de la cabecera fija (leyenda + avance) para anclar la barra de fechas
    // justo debajo, sin importar si la leyenda se envuelve en varias líneas.
    medirStickyTop() {
      const el = this.$refs.stickyTop
      if (el && el.offsetHeight) this.stickyTopH = el.offsetHeight
    },

    crearOptionEje(datos) {
      const locale = this.$i18n.locale
      return {
        backgroundColor: 'transparent',
        animation: false,
        grid: { left: this.anchoColMaquina, right: 28, top: 18, bottom: 2 },
        xAxis: {
          type: 'time',
          position: 'top',
          min: datos.min,
          max: datos.max,
          axisLabel: {
            color: '#283139',
            fontWeight: 700,
            fontSize: 10,
            formatter: (value) => {
              const d = new Date(value)
              if (d.getHours() === 0 && d.getMinutes() === 0) {
                const dias = DIAS[locale] || DIAS.es
                const meses = MESES[locale] || MESES.es
                return `{dia|${dias[d.getDay()]} ${d.getDate()} ${meses[d.getMonth()]}}`
              }
              return `${String(d.getHours()).padStart(2, '0')}:00`
            },
            rich: { dia: { fontWeight: 800, fontSize: 11, color: '#283139' } },
          },
          splitLine: { show: false },
          axisLine: { lineStyle: { color: '#bdbdbd' } },
          axisTick: { show: true, lineStyle: { color: '#bdbdbd' } },
        },
        yAxis: { type: 'value', show: false, min: 0, max: 1 },
        series: [],
      }
    },

    crearOption(datos) {
      // Span total del eje de tiempo (sin zoom). renderPlan lo compara contra el
      // span visible para saber si hay zoom activo y decidir qué texto mostrar.
      this._fullSpanMs = Math.max(1, datos.max - datos.min)
      const ahora = Date.now()
      const mostrarAhora = ahora >= datos.min && ahora <= datos.max
      const mostrarFin = Number.isFinite(datos.finJornada)
      const locale = this.$i18n.locale
      const tNow = this.$t('Reports.ProductionPlan.chart.now')
      const tEndShift = this.$t('Reports.ProductionPlan.chart.endShift')
      // Etiquetas del tooltip traducidas (se recapturan en cada render; currentLocale() repinta).
      const L = {
        real: this.$t('Reports.ProductionPlan.detail.realAdjusted'),
        plan: this.$t('Reports.ProductionPlan.detail.theoreticalPlan'),
        order: this.$t('Reports.ProductionPlan.detail.orderAbbr'),
        machine: this.$t('Reports.ProductionPlan.tooltip.machine'),
        mold: this.$t('Reports.ProductionPlan.detail.mold'),
        start: this.$t('Reports.ProductionPlan.detail.start'),
        end: this.$t('Reports.ProductionPlan.detail.end'),
        realTime: this.$t('Reports.ProductionPlan.tooltip.realTime'),
        scheduledTime: this.$t('Reports.ProductionPlan.tooltip.scheduledTime'),
        pieces: this.$t('Reports.ProductionPlan.tooltip.pieces'),
        gap: this.$t('Reports.ProductionPlan.tooltip.gap'),
        target: this.$t('Reports.ProductionPlan.tooltip.target'),
        onTarget: this.$t('Reports.ProductionPlan.band.onTarget'),
        efficiency: this.$t('Reports.ProductionPlan.detail.efficiency'),
        machineAbbr: this.$t('Reports.ProductionPlan.tooltip.machineAbbr'),
      }

      return {
        backgroundColor: '#fff',
        animation: false,

        tooltip: {
          trigger: 'item',
          borderColor: '#e6e9ed',
          formatter: (params) => {
            const v = params.value || []
            const esReal = params.seriesName === 'Real'
            const molde = esReal ? v[8] : v[5]
            const tipo = esReal ? L.real : L.plan
            const maquina = datos.etiquetas[v[0]]
            // Encabezado: tipo + Máq. + molde en una sola línea.
            const lineas = [
              `<b>${tipo} · ${L.machineAbbr} ${maquina} · ${molde || '—'}</b>`,
              `${L.start}: ${fmtFechaHora(v[1], locale)}`,
              `${L.end}: ${fmtFechaHora(v[2], locale)}`,
            ]
            if (esReal) {
              const ok = Number(v[4] || 0)
              const obj = Number(v[5] || 0)
              const raw = (params.data && params.data.raw) || {}
              // Desfase de TIEMPO (real vs teórico) calculado en el backend.
              const desfaseSeg = Number(prop(raw, 'desfaseSegundos') || 0)
              const desfasePct = Number(prop(raw, 'desfasePorcentaje') || 0)
              const tiempo = desfaseSeg > 0 ? fmtDuracion(desfaseSeg) : L.onTarget
              const color = v[6]
              // La etiqueta de banda la manda el backend (configurable); si no llega, se
              // traduce por su clave para respetar el idioma.
              const bandaKeyMap = { verde: 'onTarget', amarillo: 'off1to10', naranja: 'off10to15', rojo: 'offOver15' }
              const banda = prop(raw, 'bandaEtiqueta')
                || this.$t('Reports.ProductionPlan.band.' + bandaKeyMap[bandaKey(ok, obj, prop(raw, 'color'))])
              // Tiempo real transcurrido (geometría de la barra) vs tiempo teórico de la ODT.
              const durRealSeg = Math.max(0, Math.round((v[2] - v[1]) / 1000))
              const durTeoSeg = Number(prop(raw, 'duracionTeoricaSegundos') || 0)
              lineas.push(`${L.realTime}: ${fmtDuracion(durRealSeg)}`)
              if (durTeoSeg > 0) lineas.push(`${L.scheduledTime}: ${fmtDuracion(durTeoSeg)}`)
              lineas.push(`${L.pieces}: ${fmtNum(ok)} / ${fmtNum(obj)}`)
              lineas.push(`${L.gap}: ${desfasePct.toFixed(1)}% (${tiempo})`)
              lineas.push(
                `<span style="display:inline-block;width:10px;height:10px;border-radius:2px;` +
                `background:${color};margin-right:5px;vertical-align:middle"></span>${banda}`
              )
            } else {
              lineas.push(`${L.target}: ${fmtNum(v[4])} ${this.$t('Reports.ProductionPlan.summary.pieces')}`)
              const rawPlan = (params.data && params.data.raw) || {}
              const holguraSeg = Number(prop(rawPlan, 'holguraSegundos') || 0)
              // "Tiempo programado" = duración BASE al 100%. Fallback: total - ajuste.
              const totalSeg = Math.max(0, Math.round((v[2] - v[1]) / 1000))
              let baseSeg = Number(prop(rawPlan, 'duracionTeoricaSegundos') || 0)
              if (!(baseSeg > 0)) baseSeg = Math.max(0, totalSeg - holguraSeg)
              lineas.push(`${L.scheduledTime}: ${fmtDuracion(baseSeg)}`)
              // Eficiencia configurada para el plan. Muestra la duración TOTAL
              // (base / eficiencia) = el tiempo que tomaría trabajando a ese porcentaje.
              if (holguraSeg > 0) {
                const efic = Number(this.configReporte && this.configReporte.porcentajeHolgura) || 0
                lineas.push(`${L.efficiency} ${efic}%: ${fmtDuracion(totalSeg)}`)
              }
            }
            // La OT (clave) va hasta abajo.
            lineas.push(`${L.order}: ${v[3]}`)
            return lineas.join('<br/>')
          },
        },

        grid: { left: this.anchoColMaquina, right: 28, top: 24, bottom: 58 },

        dataZoom: [
          { type: 'inside', xAxisIndex: 0, filterMode: 'none' },
          { type: 'slider', xAxisIndex: 0, height: 20, bottom: 20, filterMode: 'none' },
        ],

        xAxis: {
          type: 'time',
          position: 'top',
          min: datos.min,
          max: datos.max,
          // Etiquetas ocultas: las fechas las pinta la barra fija (pp-eje-fecha) para que queden
          // ancladas al hacer scroll. Aquí se conservan las líneas verticales de guía.
          axisLabel: { show: false },
          splitLine: { show: true, lineStyle: { color: '#e6e9ed' } },
          axisLine: { lineStyle: { color: '#bdbdbd' } },
        },

        yAxis: {
          type: 'category',
          inverse: true,
          data: datos.etiquetas,
          axisTick: { show: false },
          axisLine: { show: false },
          splitLine: { show: false },
          axisLabel: {
            // Ancla a la izquierda: align 'left' + margin grande hace que todas las
            // etiquetas arranquen al mismo x (pegadas al borde izquierdo), sin importar su largo.
            align: 'left',
            margin: this.anchoColMaquina - 12,
            formatter: (name) => `{nom|${name}}`,
            rich: {
              nom: { color: '#283139', fontSize: 15, fontWeight: 800, align: 'left' },
            },
          },
        },

        series: [
          this.serie('Plan', datos.plan, 1, this.renderPlan),
          this.serie('Real', datos.real, 3, this.renderReal),
          {
            name: 'Ahora',
            type: 'line',
            data: [],
            silent: true,
            markLine: {
              symbol: 'none',
              label: {
                show: mostrarAhora,
                formatter: tNow,
                color: '#fff',
                backgroundColor: COLOR.ahora,
                padding: [3, 7],
                borderRadius: 3,
                fontWeight: 700,
              },
              lineStyle: { color: COLOR.ahora, type: 'dashed', width: 2 },
              data: mostrarAhora ? [{ xAxis: ahora }] : [],
            },
          },
          {
            name: 'Fin Jornada',
            type: 'line',
            data: [],
            silent: true,
            markLine: {
              symbol: 'none',
              label: {
                show: mostrarFin,
                formatter: tEndShift,
                color: '#fff',
                backgroundColor: COLOR.finJornada,
                padding: [3, 7],
                borderRadius: 3,
                fontWeight: 700,
              },
              lineStyle: { color: COLOR.finJornada, type: 'solid', width: 3 },
              data: mostrarFin ? [{ xAxis: datos.finJornada }] : [],
            },
          },
        ],
      }
    },

    serie(nombre, data, z, renderItem) {
      return {
        name: nombre,
        type: 'custom',
        z,
        encode: { x: [1, 2], y: 0 },
        data,
        renderItem,
      }
    },

    // Geometría compartida: teórico de DOBLE altura (arriba), real de altura simple
    // pegada a la parte inferior. El teórico pinta su dato en la mitad superior, que
    // la barra real no cubre.
    geometriaFila(p0, rowH) {
      const gap = rowH * 0.1
      const realH = rowH * 0.3
      const planH = realH * 2
      const bottomY = p0[1] + rowH / 2 - gap
      return { realH, planH, bottomY }
    },

    renderPlan(params, api) {
      const cat = api.value(0)
      const p0 = api.coord([api.value(1), cat])
      const p1 = api.coord([api.value(2), cat])
      const rowH = api.size([0, 1])[1]
      const ancho = p1[0] - p0[0]
      const { planH, realH, bottomY } = this.geometriaFila(p0, rowH)
      const y = bottomY - planH

      const rect = echarts.graphic.clipRectByRect(
        { x: p0[0], y, width: ancho, height: planH },
        { x: params.coordSys.x, y: params.coordSys.y, width: params.coordSys.width, height: params.coordSys.height }
      )
      if (!rect) return null

      const children = [
        {
          type: 'rect',
          shape: { x: rect.x, y: rect.y, width: rect.width, height: rect.height, r: 6 },
          style: { fill: COLOR.planFill, stroke: COLOR.planBorde, lineWidth: 1 },
        },
      ]

      // ODT crítica (TrabajoOrden.OTCritica): margen rojo grande alrededor del plan teórico.
      if (api.value(6) === 1) {
        const off = 5
        children.push({
          type: 'rect',
          shape: { x: rect.x - off, y: rect.y - off, width: rect.width + off * 2, height: rect.height + off * 2, r: 9 },
          style: { fill: 'transparent', stroke: BANDA_COLOR.rojo, lineWidth: 5 },
        })
      }

      if (ancho > 64) {
        const molde = api.value(5) || api.value(3)
        // Tiempo programado del fragmento = duración de la barra (fin - inicio).
        const durSeg = Math.max(0, Math.round((api.value(2) - api.value(1)) / 1000))
        // ¿Hay zoom activo? Comparamos el span de tiempo visible (deducido de la
        // escala píxeles/ms de esta barra) contra el span total del eje. Sin zoom
        // solo mostramos el molde; el detalle de piezas y duración aparece al hacer zoom.
        const durMs = Math.max(1, api.value(2) - api.value(1))
        const spanVisibleMs = (params.coordSys.width * durMs) / Math.max(1, ancho)
        const hayZoom = spanVisibleMs < (this._fullSpanMs || Infinity) * 0.98
        const pzas = this.$t('Reports.ProductionPlan.summary.pieces')
        const texto = hayZoom
          ? `${molde}      ${fmtNum(api.value(4))} ${pzas}      ${fmtDuracion(durSeg)}`
          : `${molde}`
        // Dato en la mitad SUPERIOR (no la tapa la barra real). Fuente escalada al alto de fila
        // para que en pantalla completa comprimida no se encime con el badge de la barra real.
        const fsPlan = Math.max(8, Math.min(11, Math.round(rowH * 0.26)))
        children.push({
          type: 'text',
          style: {
            x: rect.x + 7,
            y: rect.y + realH / 2,
            text: texto,
            fill: COLOR.planTexto,
            fontSize: fsPlan,
            fontWeight: 700,
            textVerticalAlign: 'middle',
            width: rect.width - 12,
            overflow: 'truncate',
          },
        })
      }

      return { type: 'group', children }
    },

    renderReal(params, api) {
      const cat = api.value(0)
      const p0 = api.coord([api.value(1), cat])
      const p1 = api.coord([api.value(2), cat])
      const rowH = api.size([0, 1])[1]
      const ancho = p1[0] - p0[0]
      const { realH, bottomY } = this.geometriaFila(p0, rowH)
      const y = bottomY - realH // pegada a la parte inferior

      const rect = echarts.graphic.clipRectByRect(
        { x: p0[0], y, width: ancho, height: realH },
        { x: params.coordSys.x, y: params.coordSys.y, width: params.coordSys.width, height: params.coordSys.height }
      )
      if (!rect) return null

      const color = api.value(6)
      const enCurso = api.value(7) === 1
      const finalizada = api.value(9) === 1
      const children = []

      // Marco EXTERIOR de la barra (fuera del relleno, para no taparlo y verse siempre):
      // verde y más grueso si la ODT está finalizada (tiene prioridad sobre el azul),
      // azul si solo está en curso. El relleno de desfase se conserva intacto.
      const marcoColor = finalizada ? COLOR.finalizada : (enCurso ? COLOR.azul : null)
      if (marcoColor) {
        const off = finalizada ? 4.5 : 3
        children.push({
          type: 'rect',
          shape: { x: rect.x - off, y: rect.y - off, width: rect.width + off * 2, height: rect.height + off * 2, r: 8 },
          style: { fill: 'transparent', stroke: marcoColor, lineWidth: finalizada ? 5 : 2.5 },
        })
      }

      children.push({
        type: 'rect',
        shape: { x: rect.x, y: rect.y, width: rect.width, height: rect.height, r: 5 },
        style: { fill: color },
      })

      if (ancho > 58) {
        const badgeW = 52
        const molde = api.value(8) || api.value(3)
        // Badge del molde: legible pero contenido en la fila. Se centra en la barra real y puede
        // crecer hasta la mitad del alto de fila (el margen que no invade la fila de abajo), así
        // el molde se lee aunque las filas estén comprimidas en pantalla completa.
        const cy = rect.y + rect.height / 2
        const badgeH = Math.min(16, Math.max(rect.height, rowH * 0.5))
        const fsMolde = Math.max(8, Math.min(10, Math.round(badgeH * 0.7)))
        children.push({
          type: 'rect',
          shape: { x: rect.x + 4, y: cy - badgeH / 2, width: badgeW, height: badgeH, r: badgeH / 2 },
          style: { fill: 'rgba(0,0,0,0.28)' },
        })
        children.push({
          type: 'text',
          style: {
            x: rect.x + 4 + badgeW / 2,
            y: cy,
            text: molde,
            fill: '#ffffff',
            fontSize: fsMolde,
            fontWeight: 800,
            textAlign: 'center',
            textVerticalAlign: 'middle',
            width: badgeW - 6,
            overflow: 'truncate',
          },
        })
        // Sin zoom solo dejamos el molde; las piezas (ok / objetivo) aparecen al hacer zoom.
        const durMs = Math.max(1, api.value(2) - api.value(1))
        const spanVisibleMs = (params.coordSys.width * durMs) / Math.max(1, ancho)
        const hayZoom = spanVisibleMs < (this._fullSpanMs || Infinity) * 0.98
        if (hayZoom && ancho > badgeW + 120) {
          children.push({
            type: 'text',
            style: {
              x: rect.x + 4 + badgeW + 16,
              y: rect.y + rect.height / 2,
              text: `${fmtNum(api.value(4))} / ${fmtNum(api.value(5))} ${this.$t('Reports.ProductionPlan.summary.pieces')}`,
              fill: '#fff',
              fontSize: Math.max(7, fsMolde + 1),
              fontWeight: 700,
              textVerticalAlign: 'middle',
              width: rect.width - badgeW - 34,
              overflow: 'truncate',
            },
          })
        }
      }

      return { type: 'group', children }
    },

    onClickFragmento(p) {
      if (p.seriesName !== 'Plan' && p.seriesName !== 'Real') return
      const v = p.value || []
      const raw = (p.data && p.data.raw) || {}
      const esReal = p.seriesName === 'Real'

      const objetivo = esReal ? Number(v[5] || 0) : Number(v[4] || 0)
      const ok = esReal ? Number(v[4] || 0) : 0
      const noOk = Number(prop(raw, 'piezasNoOk') || 0)
      const faltantes = Math.max(0, objetivo - ok)
      const cumplimiento = objetivo > 0 ? Math.round((ok / objetivo) * 100) : 0
      const durSeg = Math.max(0, Math.round((v[2] - v[1]) / 1000))
      const tc = Number(prop(raw, 'tc') || 0)
      const tcReal = esReal && ok > 0 && durSeg > 0 ? Math.round(durSeg / ok) : 0
      const colorHex = esReal ? colorBanda(ok, objetivo, prop(raw, 'color')) : COLOR.planFill

      // ODT "no iniciada": marca autoritativa del backend (la ODT no tiene producción real).
      // No se infiere de barrasReales porque el backend agrega un pronóstico real a TODA teórica.
      const idTrabajoOrden = Number(prop(raw, 'idTrabajoOrden') || 0)
      const iniciada = prop(raw, 'iniciada') === true

      // Piezas que el plan esperaba producidas HASTA AHORA para esta OT: la calcula el backend y la
      // expone en la barra real (null si no hay barra teórica correspondiente).
      const progAhoraRaw = esReal ? prop(raw, 'piezasProgramadasHastaAhora') : null
      const programadasAhora = progAhoraRaw == null ? null : Math.round(Number(progAhoraRaw))

      this.fragmento = {
        esReal,
        idTrabajoOrden,
        idMolde: Number(prop(raw, 'idMolde') || 0),
        noIniciada: !esReal && idTrabajoOrden > 0 && !iniciada,
        maquina: this.datos.etiquetas[v[0]],
        area: prop(raw, 'area') || '—',
        molde: prop(raw, 'molde') || (esReal ? v[8] : v[5]) || '—',
        clave: prop(raw, 'clave') || v[3] || '—',
        articulo: prop(raw, 'articulo') || '—',
        inicio: fmtFechaHora(v[1], this.$i18n.locale),
        fin: fmtFechaHora(v[2], this.$i18n.locale),
        duracion: fmtDuracion(durSeg),
        // Holgura (solo fragmentos teóricos): colchón de tiempo extra que el backend inyecta al
        // plan. duracionBase = duración al 100%; holgura = segundos extra por eficiencia; la
        // "duracion" de arriba ya es el total ajustado según la geometría de la barra.
        holguraSeg: !esReal ? Number(prop(raw, 'holguraSegundos') || 0) : 0,
        holgura: !esReal ? fmtDuracion(Number(prop(raw, 'holguraSegundos') || 0)) : '',
        duracionBase: !esReal ? fmtDuracion(Number(prop(raw, 'duracionTeoricaSegundos') || 0)) : '',
        // Tiempo teórico planificado de la ODT CON HOLGURA (duración base + colchón): es la
        // referencia contra la que el backend mide el desfase. En las barras reales el backend
        // ya lo expone ajustado; en las teóricas es la propia duración del fragmento.
        duracionTeorica: esReal
          ? (Number(prop(raw, 'duracionTeoricaSegundos') || 0) > 0
            ? fmtDuracion(Number(prop(raw, 'duracionTeoricaSegundos')))
            : null)
          : fmtDuracion(durSeg),
        objetivo,
        programadasAhora,
        ok,
        noOk,
        faltantes,
        cumplimiento,
        cumplimientoBar: Math.min(100, cumplimiento),
        tc: tc || 0,
        tcReal,
        // Desfase de TIEMPO (real vs teórico CON HOLGURA) que envía el backend.
        desfaseSeg: esReal ? Number(prop(raw, 'desfaseSegundos') || 0) : 0,
        desfasePct: esReal ? Number(prop(raw, 'desfasePorcentaje') || 0) : 0,
        enCurso: esReal ? v[7] === 1 : false,
        // OT finalizada (algún TrabajoOrdenDetalle activo en EstadoProduccion 5): marca del backend.
        finalizada: prop(raw, 'finalizada') === true || (esReal && v[9] === 1),
        colorHex,
        bandaKey: esReal ? bandaKey(ok, objetivo, prop(raw, 'color')) : '',
        // Etiqueta de banda configurable que manda el backend (tiene prioridad en bandaLabel).
        bandaEtiqueta: esReal ? (prop(raw, 'bandaEtiqueta') || '') : '',
        // Detalle por operación (cavidad). Plan: solo esperadas; Real: + hechas/ok/noOk.
        operaciones: (prop(raw, 'operaciones') || []).map((o) => ({
          idOperacion: prop(o, 'idOperacion'),
          codigo: prop(o, 'codigo'),
          tc: Number(prop(o, 'tc') || 0),
          esperadas: Number(prop(o, 'cantidad') || 0),
          ok: Number(prop(o, 'piezasOk') || 0),
          noOk: Number(prop(o, 'piezasNoOk') || 0),
          totales: Number(prop(o, 'piezasTotales') || 0),
        })),
      }
      this.dialogFragmento = true
    },
  },
}
</script>

<style scoped>
.board-pp {
  --ink: #283139;
  --muted: #6b7682;
  --line: #e6e9ed;
  --blue: #2563d6;
  background: #fff;
  padding: 16px 18px 22px;
}

/* Pantalla completa: la tarjeta llena la pantalla y permite scroll de todo el contenido */
.board-pp:fullscreen,
.board-pp:-webkit-full-screen {
  background: #fff;
  overflow: auto;
  width: 100vw;
  height: 100vh;
  border-radius: 0 !important;
  box-shadow: none !important;
  position: relative;
}

/* En pantalla completa el botón "Salir" no debe robar una fila: flota arriba a la derecha.
   La fila de filtros/toggle está oculta, así que la toolbar queda vacía y colapsa. */
.board-pp:fullscreen .toolbar,
.board-pp:-webkit-full-screen .toolbar {
  min-height: 0;
  overflow: visible;
}

.board-pp:fullscreen .tb-actions,
.board-pp:-webkit-full-screen .tb-actions {
  position: absolute;
  top: 12px;
  right: 16px;
  z-index: 10;
}

.board-pp:fullscreen .pp-toprow,
.board-pp:-webkit-full-screen .pp-toprow {
  margin-top: 0;
  /* reserva el ancho del botón flotante para que no tape la card */
  padding-right: 100px;
}

.board-pp:fullscreen .legend,
.board-pp:-webkit-full-screen .legend {
  margin: 6px 2px 2px;
}

.board-pp:fullscreen .grafica-box,
.board-pp:-webkit-full-screen .grafica-box {
  margin-top: 6px;
  padding-top: 4px;
}

/* El fondo detrás del elemento en pantalla completa (por defecto es negro) */
.board-pp:fullscreen::backdrop {
  background: #fff;
}

.board-pp:-webkit-full-screen::backdrop {
  background: #fff;
}

.pp-fs-btn {
  height: 40px !important;
  border-radius: 10px !important;
}

/* Toolbar */
.toolbar {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: nowrap;
  overflow-x: auto;
}

/* Timer + botones (config + pantalla completa) a la derecha de la fila. */
.tb-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}

/* Filtros dentro de la toolbar: sin margen superior, alineados en la misma fila. */
.toolbar .filtros {
  margin: 0;
}

.seg-toggle {
  display: inline-flex;
  background: #eef1f4;
  border-radius: 999px;
  padding: 3px;
}

.seg-toggle button {
  border: 0;
  background: transparent;
  padding: 7px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: var(--muted);
  cursor: pointer;
}

.seg-toggle button.act {
  background: var(--blue);
  color: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .15);
}

.orden-note {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
}

.auto-refresh {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  font-size: 13px;
  font-weight: 700;
  color: var(--muted);
  background: #f1f4f8;
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 0 14px;
  white-space: nowrap;
}

.auto-refresh .v-icon {
  color: var(--blue);
}

.auto-refresh.is-loading {
  color: var(--muted);
  background: #f1f3f6;
  border-color: var(--line);
}

.auto-refresh .spin {
  animation: pp-spin 1s linear infinite;
}

@keyframes pp-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* ===== Dos tarjetas resumen en el MISMO renglón:
   (Planta + Máquinas activas) | (Avance de piezas + Programadas al momento). ===== */
.pp-toprow {
  display: flex;
  align-items: stretch;
  gap: 12px;
  flex-wrap: nowrap;
  margin-top: 4px;
}

/* Cada tarjeta: fondo blanco, borde gris claro, radio ~10px y sombra muy ligera. */
.pp-topcard {
  display: flex;
  align-items: center;
  gap: 8px 18px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(20, 40, 80, .06);
  padding: 8px 16px;
  min-width: 0;
}

/* La tarjeta de avance es la más grande de la fila (40% / 60%): son los números que se leen
   primero y la barra necesita el ancho. */
.pp-topcard--maq {
  flex: 4 1 0;
}

.pp-topcard--avance {
  flex: 6 1 0;
}

.pp-sec {
  display: flex;
  flex-direction: column;
  gap: 4px;
  /* Sin min-width:0: así una sección no se encoge por debajo de su contenido; cuando no
     cabe, la fila baja a otra línea (flex-wrap) en vez de que el texto se encime. */
}

.pp-sec--planta {
  flex: 0 0 auto;
  cursor: pointer;
}

.pp-sec--maq {
  flex: 1 1 auto;
  cursor: pointer;
}

/* Avance: toma parte del espacio sobrante para estirar la barra, pero menos que antes
   para dejarle más aire a "Máquinas activas". Sin min-width fijo: si no cabe, baja de fila. */
.pp-sec--avance {
  flex: 1 1 200px;
}

.pp-sec--prog {
  flex: 0 0 auto;
}

/* Título de cada sección (Planta / Máquinas activas / Avance pzas / Programadas). */
.pp-sec-title {
  color: var(--ink);
  font-size: 11px;
  font-weight: 700;
  line-height: 1.1;
  text-transform: uppercase;
  letter-spacing: .03em;
  white-space: nowrap;
}

.pp-sec-title--center {
  text-align: center;
}

/* "Programadas al momento": etiqueta en peso normal (el número va en negritas). */
.pp-sec-title--normal {
  font-weight: 400;
}

.pp-sec-loader {
  align-self: flex-start;
  min-height: 28px;
}

/* Avance: piezas · barra · porcentaje en una misma línea. */
.pp-avance-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

/* Responsive: al angostarse, la barra roja desaparece; siempre quedan las piezas y el
   porcentaje (lo esencial). Las secciones que no caben bajan a otra fila sin encimarse. */
@media (max-width: 1100px) {
  .rs-bar {
    display: none;
  }
}

.filtros {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 auto;
  flex-wrap: nowrap;
}

/* Inputs de filtro: se reparten el ancho disponible (crecen por igual) en lugar de un
   ancho fijo, y bajan a otra fila en pantallas chicas. El texto va como label dentro. */
.filtros .f-input {
  flex: 1 1 130px;
  width: auto;
  min-width: 120px;
}

.filtros .f-input--areas {
  flex: 1.3 1 150px;
  min-width: 130px;
}

/* Todos los controles del filtro comparten el mismo lenguaje: alto 40px, radio 10px
   y borde neutro que vira a azul al enfocar. */
.filtros .f-input >>> .v-input__slot {
  border-radius: 10px !important;
}

.filtros .f-input >>> fieldset {
  border-radius: 10px;
  border-color: var(--line);
}

.filtros .f-input.v-input--is-focused >>> fieldset {
  border-color: var(--blue);
}

/* Datepicker CTK (Inicio / Fin): mismo alto, radio y borde que los selects. */
.filtros .f-input >>> .field-input {
  height: 40px !important;
  min-height: 40px !important;
  border-radius: 10px !important;
  border: 1px solid var(--line) !important;
  box-shadow: none !important;
  font-size: 13px !important;
  color: var(--ink) !important;
}

.filtros .f-input >>> .field.is-focused .field-input {
  border-color: var(--blue) !important;
}

/* "Vista": etiqueta y valor EN LÍNEA (Vista · Tiempo Real), no la etiqueta arriba.
   El recuadro (borde/alto/radio) lo dibuja .f-sel; el v-select interno va sin borde propio. */
.filtros .f-sel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  height: 40px;
  padding: 0 8px 0 12px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
}

.filtros .f-sel:focus-within {
  border-color: var(--blue);
}

/* Etiqueta "Vista" y valor "Tiempo Real": mismo color, tamaño y peso, en la misma línea. */
.filtros .f-sel-lbl {
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 600;
  color: var(--muted);
  line-height: 1;
  white-space: nowrap;
  pointer-events: none;
}

.filtros .f-sel-input {
  flex: 1 1 auto;
  min-width: 0;
}

/* El v-select interno pierde su recuadro/alto propio: el marco lo pone .f-sel. */
.filtros .f-sel >>> .v-input__slot {
  min-height: 0 !important;
  padding: 0 !important;
}

.filtros .f-sel >>> fieldset {
  border: none !important;
}

.filtros .f-sel >>> .v-select__selections {
  padding: 0 !important;
  min-height: 0 !important;
  justify-content: flex-end;
}

/* El valor seleccionado igualado a la etiqueta (mismo color/tamaño/peso). */
.filtros .f-sel >>> .v-select__selection,
.filtros .f-sel >>> .v-select__selections input {
  margin: 0 !important;
  padding: 0 !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  line-height: 1 !important;
  color: var(--muted) !important;
}

.filtros .f-sel >>> .v-input__append-inner {
  margin-top: 0 !important;
  padding-left: 2px !important;
  align-self: center;
}

.f-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.f-field.f-grow {
  flex: 1;
  min-width: 140px;
}

.f-field label {
  font-size: 11px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: .03em;
}

.f-field.f-grow {
  min-width: 200px;
}

/* Filtro de áreas: más corto, no crece a lo ancho como los demás. */
.f-field.f-areas {
  flex: 0 1 260px;
  min-width: 170px;
  max-width: 300px;
}

/* Datepicker: el activador (v-text-field) ocupa el mismo ancho que los selects */
.f-field>>>.v-text-field {
  min-width: 150px;
}

/* Overrides del v-date-picker: el _tables.scss global rompe la tabla del
   calendario (filas/colores). Estas reglas la neutralizan, igual que en
   el componente MenuDatepicker. */
.pp-date-picker>>>.v-btn--rounded {
  border-radius: 28px !important;
}

.pp-date-picker>>>.v-btn.v-btn--disabled {
  color: rgba(0, 0, 0, .26) !important;
}

.pp-date-picker>>>.v-btn {
  color: rgba(0, 0, 0, 0.87) !important;
  font-family: Roboto, sans-serif;
  font-weight: 500 !important;
  padding: 0;
}

.pp-date-picker>>>.v-date-picker-table .v-btn.v-btn--active {
  color: #fff !important;
}

.pp-date-picker>>>.accent {
  background-color: #005caf !important;
  border-color: #005caf !important;
}

.pp-date-picker>>>.accent--text {
  color: #005caf !important;
  caret-color: #005caf !important;
}

.pp-date-picker>>>.v-btn__content {
  font-family: Roboto, sans-serif;
  font-weight: 500 !important;
  flex-grow: 1;
}

.pp-date-picker>>>tbody tr:nth-of-type(odd) {
  background-color: transparent;
}

.pp-date-picker>>>.v-btn--active:before,
.pp-date-picker>>>.v-btn:focus:before,
.pp-date-picker>>>.v-btn:hover:before {
  background-color: rgb(85, 85, 85);
}

.pp-date-picker>>>.v-date-picker-header__value button {
  font-size: 1rem;
}

.f-field>>>.v-input {
  margin: 0;
  padding: 0;
  font-size: 13px;
}

.f-field>>>.v-input__control {
  min-height: 34px;
}

.f-field>>>.v-select__selections {
  min-height: 32px;
}

.f-aplicar {
  align-self: center;
  height: 40px !important;
  border-radius: 10px !important;
  flex: 0 0 auto;
}

.tb-right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-grp {
  display: flex;
  align-items: center;
  gap: 6px;
}

.fg-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--muted);
}

.chip {
  border: 1px solid var(--line);
  background: #fff;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--ink);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
}

.chip.act {
  border-color: var(--blue);
  color: var(--blue);
  background: #f0f5ff;
  font-weight: 600;
}

.pop {
  background: #fff;
  padding: 8px 12px;
  display: grid;
  gap: 2px;
  max-height: 320px;
  overflow: auto;
}

.pop-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  padding: 3px 0;
  cursor: pointer;
  color: var(--ink);
}

.pop-item.pop-all {
  border-bottom: 1px solid var(--line);
  padding-bottom: 6px;
  margin-bottom: 4px;
  font-weight: 600;
}

/* KPI Máquinas activas (réplica de ReporteCumplimientoProduccion) */
.pp-kpi-grid {
  margin: 0 0 14px;
}

.cp-kpi-grid {
  display: grid;
  align-items: stretch;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.cp-kpi-card {
  background: linear-gradient(180deg, #f8fbff 0%, #f2f6fc 100%);
  border: 1px solid #c4d5ea;
  border-radius: 22px;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: flex-start;
  min-height: 130px;
  padding: 14px 16px 12px;
}

.cp-kpi-card--blue {
  border-color: #8fc1f3;
}

.cp-kpi-top {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.cp-kpi-top-main {
  align-items: center;
  display: flex;
  gap: 12px;
  min-width: 0;
}

.cp-kpi-eye-btn {
  height: 26px !important;
  margin-left: 8px;
  width: 26px !important;
}

.cp-kpi-eye-icon {
  color: #9aa6b8 !important;
  font-size: 1.1rem !important;
}

.cp-kpi-icon-pack {
  align-items: center;
  border-radius: 16px;
  display: inline-flex;
  height: 52px;
  justify-content: center;
  width: 52px;
}

.cp-kpi-icon-pack--blue {
  background: linear-gradient(145deg, #d8e9fb, #c6def7);
}

.cp-kpi-icon-main {
  color: #2e8aed !important;
  font-size: 1.45rem !important;
}

.cp-kpi-title {
  color: #1c2639;
  font-size: clamp(1.05rem, 1.1vw, 1.3rem);
  font-weight: 700;
  line-height: 1.15;
  margin: 0;
  text-align: left;
}

.cp-kpi-value-row {
  align-items: baseline;
  display: flex;
  gap: 8px;
  justify-content: center;
  margin: 8px 0 4px;
  text-align: center;
  width: 100%;
}

.cp-kpi-value-main {
  font-size: 52px !important;
  font-weight: 800;
  line-height: 0.9;
  margin: 0;
}

.cp-kpi-value-main--blue {
  color: #2d84da;
}

/* Spinner que reemplaza al número mientras cargan las máquinas activas. La fila usa
   align-items: baseline; lo centramos para que no quede desalineado con el "/ total". */
.cp-kpi-value-loader {
  align-self: center;
}

.cp-kpi-value-secondary {
  color: #667791;
  font-size: 27px !important;
  font-weight: 700;
  line-height: 1;
  margin: 0;
}

.cp-kpi-caption {
  color: #657993;
  font-size: clamp(0.9rem, 0.95vw, 1rem);
  font-weight: 500;
  line-height: 1.1;
  margin: auto 0 0;
}

/* ---- Variante horizontal del KPI: sin ícono, con los elementos repartidos a lo
   ancho (título + ojo · valor · caption). Prioriza el uso del ancho sobre la altura. ---- */
.cp-kpi-card.cp-kpi-card--row {
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  min-height: 0;
  padding: 10px 18px;
  cursor: pointer;
}

.cp-kpi-head {
  align-items: center;
  display: flex;
  gap: 4px;
  flex: 0 1 auto;
  min-width: 0;
}

.cp-kpi-card--row .cp-kpi-title {
  font-size: 1rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cp-kpi-card--row .cp-kpi-eye-btn {
  margin-left: 0;
}

.cp-kpi-card--row .cp-kpi-value-row {
  align-items: baseline;
  gap: 6px;
  justify-content: center;
  margin: 0;
  width: auto;
  flex: 0 0 auto;
  white-space: nowrap;
}

.cp-kpi-card--row .cp-kpi-value-main {
  font-size: 30px !important;
  line-height: 1;
}

.cp-kpi-card--row .cp-kpi-value-secondary {
  font-size: 20px !important;
}

.cp-kpi-card--row .cp-kpi-caption {
  font-size: 0.85rem;
  margin: 0;
  white-space: nowrap;
}

/* ---- Sección "Máquinas activas": naves en fila, compactas y sin divisores. El número
   (activas/esperadas) va en azul y el nombre de la nave debajo. ---- */
/* Naves distribuidas de forma simétrica dentro de la sección, para que el título
   (centrado) siempre quede en medio, ya sean 2, 3 o 4 áreas. */
.pp-ma-cols {
  display: flex;
  align-items: flex-start;
  justify-content: space-evenly;
  gap: 4px 18px;
  flex-wrap: wrap;
  width: 100%;
}

.pp-ma-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 38px;
}

.pp-ma-val {
  color: #2d84da;
  font-size: 18px;
  font-weight: 800;
  line-height: 1;
}

/* El contador de Planta resalta más que el de las naves. */
.pp-sec--planta .pp-ma-val {
  font-size: 21px;
}

.pp-ma-area {
  color: #657993;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.1;
  margin-top: 2px;
  text-align: center;
  white-space: nowrap;
}

/* Nombre de la planta: alineado a la izquierda y algo más presente que las naves. */
.pp-ma-area--planta {
  color: var(--ink);
  font-size: 11px;
  font-weight: 700;
  text-align: left;
  margin-top: 2px;
  text-transform: uppercase;
}

/* Leyenda */
/* Leyenda + avance total: se mantienen fijos en la parte superior al hacer scroll */
.pp-sticky-top {
  position: sticky;
  top: 0;
  z-index: 6;
  background: #fff;
  /* compensa el padding lateral de .board-pp para que el fondo cubra de borde a borde */
  margin: 0 -18px;
  padding: 4px 18px 2px;
}

.legend {
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  align-items: center;
  margin: 14px 2px 4px;
}

.lg {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12.5px;
  color: var(--muted);
}

.lg i {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  display: inline-block;
}

.lg i.i-plan {
  background: #dcf0d6;
  border: 1.5px solid #7cbd74;
}

/* Indicador de ODT finalizada: cuadro transparente con borde grueso verde. */
.lg i.i-finalizada {
  background: transparent;
  border: 3px solid #22a84e;
}

/* Avance de piezas: el dato principal (producidas / objetivo), resaltado. */
.rs-piezas {
  font-size: 21px;
  font-weight: 800;
  color: var(--ink);
  line-height: 1;
  letter-spacing: -.02em;
  white-space: nowrap;
}

/* Barra de progreso: horizontal, estirable y con bordes redondeados. */
.rs-bar {
  flex: 1 1 auto;
  min-width: 40px;
  height: 10px;
  border-radius: 999px;
  background: #eef1f5;
  overflow: hidden;
}

.rs-fill {
  height: 100%;
  border-radius: 999px;
  transition: width .3s ease;
}

/* Porcentaje: a la derecha de la barra, prominente (color según el desfase). */
.rs-pct {
  font-size: 19px;
  font-weight: 800;
  letter-spacing: -.02em;
  white-space: nowrap;
}

/* "Programadas al momento": fracción alineada bajo su título. */
.rs-now-val {
  font-size: 19px;
  font-weight: 540;
  color: var(--ink);
  line-height: 1;
  white-space: nowrap;
}


.cp-empty {
  padding: 40px 4px;
  color: var(--muted);
  font-size: 14px;
  text-align: center;
}

/* Caja contenedora de la gráfica */
.grafica-box {
  border: 1px solid var(--line);
  border-top: none;
  border-radius: 0 0 12px 12px;
  background: #fff;
  padding: 8px 14px;
  margin-top: 8px;
}

/* Barra de fechas (eje de tiempo) fija dentro de la caja, anclada bajo la cabecera. Comparte
   el ancho y el offset de grid del gráfico principal, por lo que las fechas quedan alineadas
   con las líneas verticales. `top` se calcula con la altura real de la cabecera fija. */
.pp-eje-fecha {
  position: sticky;
  z-index: 4;
  height: 30px;
  width: 100%;
  background: #fff;
  border-bottom: 1px solid var(--line);
}

.grafica-tablero {
  width: 100%;
  min-height: 360px;
}

.frag-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--ink);
  display: flex;
  align-items: center;
}

/* OT finalizada: marco y sombra verdes en el encabezado (Real · área) para que resalte. */
.frag-title.frag-title--fin {
  border: 2px solid #22a84e;
  border-radius: 8px;
  background: rgba(34, 168, 78, 0.10);
  box-shadow: 0 0 0 3px rgba(34, 168, 78, 0.18);
  padding: 8px 12px;
  margin: 4px 4px 0;
}

/* Los botones de acciones pueden saltar de línea para no desbordar la carta
   ni generar scroll horizontal cuando hay varias acciones visibles. */
.frag-actions {
  flex-wrap: wrap;
  row-gap: 4px;
}

.frag-actions .v-btn {
  flex: 0 1 auto;
  min-width: 0;
}

.cambio-lbl {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--muted);
  margin: 12px 0 4px;
}

.cambio-error {
  background: #fdecec;
  color: #b42318;
  border: 1px solid #f3c6c2;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
  margin-bottom: 8px;
}

/* Editor de bandas de color por desfase (tarjetas) */
.bandas-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.banda-card {
  border: 1px solid #e6e9ed;
  border-radius: 10px;
  background: #fafbfc;
  padding: 10px 12px;
}

.banda-card-top {
  display: flex;
  align-items: center;
  gap: 10px;
}

.banda-swatch {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.18);
  cursor: pointer;
  display: block;
}

.banda-rango-txt {
  font-weight: 700;
  font-size: 13.5px;
  color: var(--ink, #283139);
}

.banda-card-body {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  margin-top: 10px;
}

.banda-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.banda-field--grow {
  flex: 1 1 auto;
}

.banda-field label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7682;
}

.banda-hasta-input {
  max-width: 120px;
}

.banda-ultima {
  flex: 0 0 auto;
  font-size: 12px;
  font-style: italic;
  color: #6b7682;
  padding-bottom: 8px;
}

.frag-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #f1f3f6;
  font-size: 13.5px;
}

.frag-row:last-child {
  border-bottom: 0;
}

.frag-row span {
  color: var(--muted);
}

.frag-row b {
  color: var(--ink);
  font-weight: 700;
  text-align: right;
}

/* Fila de máquina activa: nombre con punto de estado + badge Con/Sin OT + detalle */
.act-name {
  align-items: center;
  display: inline-flex;
  gap: 8px;
}

.act-dot {
  border-radius: 50%;
  display: inline-block;
  height: 9px;
  width: 9px;
  flex: 0 0 auto;
}

.act-dot--yes {
  background: #63b46a;
}

.act-dot--no {
  background: #d64545;
}

.act-right {
  align-items: center;
  display: inline-flex;
  gap: 10px;
}

.act-ot {
  border-radius: 999px;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 10px;
  white-space: nowrap;
  letter-spacing: .02em;
}

.act-ot--yes {
  background: #63b46a;
}

.act-ot--no {
  background: #d64545;
}

/* Fila de máquina programada que ya arrancó la OT señalada: resaltado sutil */
.frag-row--arrancada {
  background: rgba(99, 180, 106, .1);
  border-radius: 6px;
}

.frag-dot {
  width: 12px;
  height: 12px;
  border-radius: 3px;
  display: inline-block;
  margin-right: 8px;
}

/* OT finalizada: marco verde grueso alrededor del indicador (respeta su color de relleno),
   igual que el borde de las barras finalizadas de la gráfica. */
.frag-dot.frag-dot--fin {
  box-shadow: 0 0 0 2px #22a84e;
}

.frag-banda {
  font-size: 12px;
  font-weight: 700;
}

.frag-rango {
  display: inline-flex;
  align-items: center;
}

.frag-sec {
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .05em;
  color: #97a0aa;
  margin: 12px 0 2px;
}

.frag-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 18px;
}

.frag-row--wide {
  grid-column: 1 / -1;
}

.frag-progress {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
}

.frag-progress-bar {
  flex: 1;
  height: 8px;
  border-radius: 5px;
  background: #e9edf2;
  overflow: hidden;
}

/* OT finalizada: marco verde alrededor de la barra de progreso (respeta el color del relleno). */
.frag-progress-bar.frag-progress-bar--fin {
  box-shadow: 0 0 0 2px #22a84e;
}

.frag-progress-fill {
  height: 100%;
  border-radius: 5px;
  transition: width .3s ease;
}

.frag-progress-txt {
  font-size: 12px;
  font-weight: 700;
  color: var(--ink);
  white-space: nowrap;
}

.frag-ops {
  width: 100%;
  border-collapse: collapse;
  margin-top: 4px;
  font-size: 12px;
}

.frag-ops th {
  text-align: left;
  font-weight: 700;
  color: #97a0aa;
  border-bottom: 1px solid var(--line);
  padding: 4px 8px;
  white-space: nowrap;
}

.frag-ops td {
  padding: 5px 8px;
  border-bottom: 1px solid #f1f3f6;
  color: var(--ink);
  font-weight: 600;
}

.frag-ops .num {
  text-align: right;
}

.frag-ops-id {
  color: #b3bcc6;
  font-weight: 600;
  font-size: 10px;
}
</style>

<!-- Popover (montado fuera del scope) -->
<style>
.pp-pop-menu {
  border: 1px solid #e6e9ed;
  border-radius: 10px;
  box-shadow: 0 8px 26px rgba(20, 30, 40, 0.14);
  background: #fff;
}

.pp-pop-menu .pop {
  background: #fff;
  padding: 8px 12px;
  display: grid;
  gap: 2px;
  max-height: 320px;
  overflow: auto;
  min-width: 180px;
}

.pp-pop-menu .pop-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  padding: 3px 0;
  cursor: pointer;
  color: #283139;
}

.pp-pop-menu .pop-item.pop-all {
  border-bottom: 1px solid #e6e9ed;
  padding-bottom: 6px;
  margin-bottom: 4px;
  font-weight: 600;
}

.pp-pop-menu .pop-item input {
  cursor: pointer;
}
</style>
